import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { del, get, patch, post, qs } from '../lib/api';
import { navigate, storageGet, storageSet, useDebounced, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { usePlan } from '../lib/plan';
import { useCart, type CartCustomer } from '../lib/cart';
import { calculateCart, formatCents, formatRatePpm } from '../../shared/money';
import { dayKey, formatDateTime, formatPhone, formatTime, isValidImei, zone } from '../../shared/format';
import {
  DEVICE_TYPE_LABELS, LEFT_WITH_BY_TYPE, LEFT_WITH_OPTIONS, LONGER_REPAIR_TIMES, OPEN_REPAIR_STATUSES, PRECHECK_ITEMS, PRECHECK_LABELS, REPAIR_STATUS_LABELS, REPAIR_TIMES,
  DROP_OFF_NOTE, defaultPartsCents, deviceTypeOf, dropOffRows, isDeviceType, isLaborOnly, isTechTicketFormat, labelWork, precheckItemsFor, precheckLabel, repairCheckoutParts, repairDeviceName,
  repairMinutesFromEstimate, repairTimeLabel, shortDeviceName,
  type DeviceType, type PrecheckItem, type PrecheckValue, type RepairStatus,
} from '../../shared/repairs';
import { MENU_TYPES_WITH_MODELS, REPAIR_MENU, REPAIR_WORDS, repairLineGroup, type ModelBrand, type PartGroup } from '../../shared/repair-menu';
import { REPAIR_TYPE_PHOTOS, repairPhotoFor } from '../../shared/photos';
import { SUPPLIERS, type SupplierId } from '../../shared/parts';
import { Badge, Button, Confirm, Cost, Empty, Field, Money, MoneyInput, Profit, Sheet, Spinner, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { DeviceArt, ProductPicture } from '../ui/art';
import { Barcode, usePrint } from '../ui/print';
import { jobText, useCloudPrint, type CloudKind, type PrintJob } from '../lib/printing';
import { ReceiptMark } from '../ui/legal';
import { photoToDataUrl, pickPhoto } from '../lib/image';
import { CustomerSearch } from './CustomerPicker';
import { ModelPhoto, Photo, PhotoTile, useModelPhoto } from '../ui/photo';
import { PayStampView, ReceiptHeader, ReceiptPolicies, SiteLine, storeLines, type StoreInfo } from './Receipt';
import { repairPayment } from '../../shared/receipts';
import { PhoneCheckButton, UnlockTools } from './PhoneCheck';
import { RepairPartsSearch, repairPartKey, type RepairPart } from './PartSearch';
import { BulkEditSheet, type BulkField, type BulkValues } from '../ui/bulk';
import { DepositSheet } from './Sale';
import { ApprovalSheet, ApprovalsCard, DiagnosisCard, JobIntakeCard, JobItemCard, JobLinesCard, WorkOrderLetter, useJobKind } from './Jobs';
import { JOB_WORDS } from '../../shared/jobs';
import { RangePicker, useRange } from './Reports';

// ---------------------------------------------------------------- shared bits

export function StatusPill({ status }: { status: RepairStatus }) {
  return <span className={`status-pill st-${status}`}>{REPAIR_STATUS_LABELS[status]}</span>;
}

function when(ms: number): string {
  const today = dayKey(Date.now());
  const d = dayKey(ms);
  if (d === today) return `today ${formatTime(ms)}`;
  if (d === dayKey(Date.now() + 86_400_000)) return `tomorrow ${formatTime(ms)}`;
  return formatDateTime(ms);
}

/** The drop-off check for one device: ✓ works, ✗ not working, ? can't test (check-in and Edit ticket). */
function PrecheckGrid({ checks, value, onChange }: { checks: PrecheckItem[]; value: Record<string, PrecheckValue>; onChange: (v: Record<string, PrecheckValue>) => void }) {
  return (
    <div className="precheck">
      {checks.map((it) => (
        <div key={it.key} className="pc">
          <span className="small strong">{it.label}</span>
          <span className="opts">
            {(['ok', 'bad', 'na'] as PrecheckValue[]).map((v) => (
              <button key={v} type="button" className={v} aria-pressed={value[it.key] === v} title={precheckLabel(it.key, v)} aria-label={`${it.label}: ${PRECHECK_LABELS[v]}`} onClick={() => onChange({ ...value, [it.key]: v })}>
                {v === 'ok' ? '✓' : v === 'bad' ? '✗' : '?'}
              </button>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

/** "All work": every check set to Works; staff then change the ones that don't. */
const allWork = (checks: PrecheckItem[]): Record<string, PrecheckValue> => Object.fromEntries(checks.map((it) => [it.key, 'ok' as PrecheckValue]));

interface HubOption { id: number; label: string; option: string; price_cents: number | null; from: boolean; time: string; warranty: string }
interface HubRepair { type: string; description: string; options: HubOption[] }

/** The website's repair prices for a model, from the WCW Hub. */
function useRepairOptions(brand: string, model: string) {
  const m = useDebounced(model.trim(), 350);
  return useLoad<{ repairs: HubRepair[]; source: string }>(
    () => (brand && m.length >= 3 ? get(`/api/repair-options${qs({ brand, model: m })}`) : Promise.resolve({ repairs: [], source: 'none' })),
    [brand, m],
  );
}

// ---------------------------------------------------------------- board

const FILTERS: { id: string; label: string; statuses: string[] }[] = [
  { id: 'open', label: 'All open', statuses: OPEN_REPAIR_STATUSES },
  { id: 'today', label: 'Made today', statuses: ['today'] },
  { id: 'checked_in', label: 'Waiting for tech', statuses: ['checked_in'] },
  { id: 'in_progress', label: 'Being repaired', statuses: ['in_progress'] },
  { id: 'waiting_parts', label: 'Waiting for parts', statuses: ['waiting_parts'] },
  { id: 'waiting_customer', label: 'Needs customer OK', statuses: ['waiting_customer'] },
  { id: 'ready', label: 'Ready for pickup', statuses: ['ready'] },
  { id: 'done', label: 'Done this week', statuses: ['picked_up', 'cancelled'] },
  // Tickets (last 90 days) with a priced repair whose part isn't on it yet: managers put the part on, for the sales tax report.
  { id: 'needs_part', label: 'Needs a part', statuses: ['needs_part'] },
  { id: 'all', label: 'All time', statuses: [] },
];

/** Today as the date box wants it (YYYY-MM-DD, this device's clock). */
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
/** "Tuesday, Oct 20, 2026" for a YYYY-MM-DD day. */
const longDate = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(Date.UTC(y, m - 1, d, 12)));
};

/** Tickets still in the store: they can be picked in the list (to mark ready, or to cancel). */
const isOpen = (r: { status: string }) => OPEN_REPAIR_STATUSES.includes(r.status as RepairStatus);
/** Tickets that can be marked ready from the list: still open, not ready yet. */
const canMarkReady = (r: { status: string }) => isOpen(r) && r.status !== 'ready';

interface OldRepairDesk { open: number; worked: number; ages: { days: number; count: number }[] }

export function RepairsScreen({ query }: { query: URLSearchParams }) {
  const { can, me, status: session } = useSession();
  const jobKind = useJobKind();
  const checkInLabel = jobKind ? JOB_WORDS[jobKind].checkIn : 'Check in a repair';
  const toast = useToast();
  const { run, busy } = useAction();
  // A day picked on the calendar shows every ticket that day touched; otherwise a status.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(query.get('date') ?? '') ? query.get('date')! : '';
  const status = date ? 'day' : query.get('status') || 'open';
  // A date range picked (a month, say, or any two dates): the total repairs for it, like Reports does.
  const [pickingRange, setPickingRange] = useState(false);
  const { range: pickedRange, setRange: setPickedRange, custom, setCustom, r: rangeR } = useRange('month');
  // Tick tickets (or all of them) and mark them ready, or cancel them, at once.
  const [picked, setPicked] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Edit the ticked tickets in one go: technician, status, ready-by time, text updates.
  const [editingMany, setEditingMany] = useState(false);
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  // Someone who works at more than one store (the owner) picks whose repairs to see.
  const [store, setStore] = useState(() => storageGet<string>('sos_repairs_store', ''));
  const stores = me?.locations ?? [];
  const storeOk = !store || store === 'all' || stores.some((l) => l.id === store);
  const location = storeOk ? store : '';
  const pickStore = (v: string) => { setStore(v); storageSet('sos_repairs_store', v); };
  // My bench: the tickets assigned to me ('me'); managers can pick another technician's. Kept per person.
  const benchKey = `sos_repairs_bench_${me?.employee.id ?? ''}`;
  const [bench, setBench] = useState(() => storageGet<string>(benchKey, ''));
  const pickBench = (v: string) => { setBench(v); storageSet(benchKey, v || null); };
  const team = session?.device?.employees ?? [];
  const list = useLoad<{ repairs: any[]; counts: Record<string, number>; lifetime?: { total: number; picked_up: number; since: number | null; from_repairdesk: number }; day?: { date: string; checked_in: number; ready: number; picked_up: number } | null; range?: { checked_in: number; ready: number; picked_up: number } | null }>(
    () => get(`/api/repairs${qs({
      status: pickingRange || date ? undefined : status, date: !pickingRange && date ? date : undefined,
      from: pickingRange ? rangeR.from : undefined, to: pickingRange ? rangeR.to : undefined,
      q: dq, location, technician: bench,
    })}`),
    [status, date, pickingRange, rangeR.from, rangeR.to, dq, location, bench],
  );
  // The owner closes the old tickets brought over from RepairDesk that still sit on the board.
  const owner = can('settings.manage');
  const oldRd = useLoad<OldRepairDesk | null>(() => (owner ? get(`/api/repairs/repairdesk/old${qs({ location })}`) : Promise.resolve(null)), [owner, location]);
  const [closingOld, setClosingOld] = useState(false);
  const many = stores.length > 1;
  const counts = list.data?.counts ?? {};
  const life = list.data?.lifetime;
  const count = (f: (typeof FILTERS)[number]) => (f.id === 'all' ? (life?.total ?? 0).toLocaleString('en-US') : f.statuses.reduce((s, k) => s + (counts[k] ?? 0), 0));
  const picking = can('repair.work');
  const pickable = (list.data?.repairs ?? []).filter(isOpen);
  const chosen = pickable.filter((r) => picked.includes(r.id));
  // Ones already ready stay as they are when the rest are marked ready.
  const toReady = chosen.filter(canMarkReady);
  const allPicked = pickable.length > 0 && chosen.length === pickable.length;
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const bulkFields: BulkField[] = [
    { key: 'technicianId', label: 'Technician', kind: 'select', options: [{ value: '-', label: 'Nobody yet' }, ...team.map((t) => ({ value: t.id, label: t.name }))] },
    { key: 'status', label: 'Status', kind: 'select', options: OPEN_REPAIR_STATUSES.map((st) => ({ value: st, label: REPAIR_STATUS_LABELS[st] })), hint: 'Ready texts the customers who agreed to texts' },
    { key: 'readyIn', label: 'Ready in', kind: 'select', options: [...REPAIR_TIMES, ...LONGER_REPAIR_TIMES].map((m) => ({ value: String(m), label: m < 60 ? `${m} minutes` : m < 1440 ? `${m / 60} hour${m === 60 ? '' : 's'}` : `${m / 1440} day${m === 1440 ? '' : 's'}` })), hint: 'from now' },
    { key: 'notifySms', label: 'Text updates', kind: 'yesno', yes: 'On', no: 'Off' },
  ];
  const applyBulk = async (item: { id: string }, v: BulkValues) => {
    const body: Record<string, unknown> = {};
    if (typeof v.technicianId === 'string') body.technicianId = v.technicianId === '-' ? null : v.technicianId;
    if (typeof v.readyIn === 'string') { const m = Number(v.readyIn); body.promisedAt = Date.now() + m * 60_000; if (([25, 35, 60] as number[]).includes(m)) body.repairMinutes = m; }
    if (typeof v.notifySms === 'boolean') body.notifySms = v.notifySms;
    if (Object.keys(body).length) await patch(`/api/repairs/${item.id}`, body);
    if (typeof v.status === 'string') {
      const res = await post<{ moved: string[]; failed: { message: string }[] }>('/api/repairs/status', { ids: [item.id], status: v.status });
      if (res.failed.length) throw new Error(res.failed[0].message);
    }
  };
  const markReady = async () => {
    const res = await run(() => post<{ moved: string[]; failed: { ticket: string | null; message: string }[]; texted: number }>('/api/repairs/status', { ids: toReady.map((r) => r.id), status: 'ready' }));
    setConfirming(false);
    if (!res) return;
    const n = res.moved.length;
    const texts = res.texted ? ` ${res.texted} customer${res.texted === 1 ? '' : 's'} texted.` : '';
    if (res.failed.length) {
      toast(`${n} marked ready.${texts} ${res.failed.length} couldn’t be: ${res.failed.slice(0, 3).map((f) => `${f.ticket ?? 'a ticket'} (${f.message})`).join('; ')}${res.failed.length > 3 ? '…' : ''}`, n ? 'info' : 'error');
    } else toast(`${n} ticket${n === 1 ? '' : 's'} marked ready.${texts}`, 'good');
    setPicked([]);
    list.reload();
  };
  return (
    <div className="page">
      <div className="page-head">
        <h1>{jobKind === 'vehicle' ? 'Work orders' : 'Repairs'}</h1>
        {oldRd.data?.open ? <Button icon="history" onClick={() => setClosingOld(true)}>Close old RepairDesk tickets</Button> : null}
        {can('repair.create') ? <Button variant="primary" icon="plus" onClick={() => navigate('/repairs/new')}>{checkInLabel}</Button> : null}
      </div>
      <div className="row-wrap">
        {many ? (
          <select className="select" style={{ width: 'auto' }} value={location} onChange={(e) => pickStore(e.target.value)} aria-label="Store">
            <option value="">{me?.location ? `This store (${me.location.name})` : 'This store'}</option>
            {stores.filter((l) => l.id !== me?.location?.id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            <option value="all">All stores</option>
          </select>
        ) : null}
        <div className="search grow" style={{ minWidth: 220 }}>
          <Icon.search />
          <input className="input input-lg" placeholder={jobKind === 'vehicle' ? 'Work order number, customer, phone, VIN or plate' : jobKind ? 'Ticket number, customer, phone or serial number' : 'Ticket number, customer, phone or IMEI'} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a repair" />
        </div>
        <button type="button" className="chip" aria-pressed={bench === 'me'} onClick={() => pickBench(bench === 'me' ? '' : 'me')} title="Only the tickets assigned to you">
          <Icon.wrench /> My bench
        </button>
        {can('employees.manage') && team.length ? (
          <select className="select" style={{ width: 'auto' }} value={bench === 'me' ? me?.employee.id ?? '' : bench} onChange={(e) => pickBench(e.target.value && e.target.value === me?.employee.id ? 'me' : e.target.value)} aria-label="Technician">
            <option value="">Every technician</option>
            {team.map((t) => <option key={t.id} value={t.id}>{t.id === me?.employee.id ? `${t.name} (me)` : t.name}</option>)}
          </select>
        ) : null}
      </div>
      <div className="chips" role="tablist" aria-label="Repair status">
        {FILTERS.filter((f) => f.id !== 'needs_part' || (can('reports.store') && !jobKind)).map((f) => (
          <button key={f.id} type="button" role="tab" className="chip" aria-pressed={status === f.id} aria-selected={status === f.id} onClick={() => { setPickingRange(false); navigate(`/repairs${f.id === 'open' ? '' : `?status=${f.id}`}`); }}>
            {f.label} <span className={status === f.id ? '' : 'muted'}>{count(f)}</span>
          </button>
        ))}
        {/* Any day on the calendar: every ticket that day touched (checked in, marked ready or picked up). */}
        <label className={`chip rp-day ${date ? 'rp-day-on' : ''}`} title="Every ticket from one day">
          <Icon.calendar />
          <input type="date" value={date} max={todayStr()} onChange={(e) => { setPickingRange(false); navigate(e.target.value ? `/repairs?date=${e.target.value}` : '/repairs'); }} aria-label="Pick a day" />
        </label>
        {date ? <button type="button" className="chip" onClick={() => navigate('/repairs')} aria-label="Back to open repairs"><Icon.close /> Clear</button> : null}
        <button type="button" className="chip" aria-pressed={pickingRange} onClick={() => setPickingRange(!pickingRange)} title="Total repairs for a month, or any two dates">
          <Icon.calendar /> Pick a range
        </button>
      </div>
      {pickingRange ? <RangePicker range={pickedRange} onRange={setPickedRange} custom={custom} onCustom={setCustom} /> : null}
      {pickingRange && list.data?.range ? (
        <div className="lifetime card">
          <div className="lt-n">{list.data.range.checked_in}</div>
          <div>
            <div className="strong">Total {jobKind === 'vehicle' ? 'work orders' : 'repairs'} for the dates picked above</div>
            <div className="small muted">{list.data.range.ready} marked ready · {list.data.range.picked_up} picked up in that span.</div>
          </div>
        </div>
      ) : null}
      {date && list.data?.day ? (
        <div className="lifetime card">
          <div className="lt-n">{list.data.repairs.length}</div>
          <div>
            <div className="strong">{jobKind === 'vehicle' ? 'Work orders' : 'Repairs'} on {longDate(date)}</div>
            <div className="small muted">{list.data.day.checked_in} checked in · {list.data.day.ready} marked ready · {list.data.day.picked_up} picked up that day{list.data.repairs.length >= 200 ? ' · showing the first 200' : ''}.</div>
          </div>
        </div>
      ) : null}
      {status === 'all' && life ? (
        <div className="lifetime card">
          <div className="lt-n">{life.total.toLocaleString('en-US')}</div>
          <div>
            <div className="strong">Lifetime repairs{life.since ? ` since ${new Date(life.since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}` : ''}</div>
            <div className="small muted">{life.picked_up.toLocaleString('en-US')} picked up{life.from_repairdesk ? ` · ${life.from_repairdesk.toLocaleString('en-US')} from RepairDesk` : ' · RepairDesk history comes in with the RepairDesk import (Settings)'}. Showing the latest {Math.min(list.data?.repairs.length ?? 0, 200)}; search to find any ticket.</div>
          </div>
        </div>
      ) : null}
      {list.loading && !list.data ? <Spinner /> : null}
      {picking && pickable.length ? (
        <div className="pick-bar" role="region" aria-label="Pick tickets">
          <label className="check" style={{ margin: 0 }}>
            <input type="checkbox" className="row-pick" checked={allPicked} onChange={() => setPicked(allPicked ? [] : pickable.map((r) => r.id))} aria-label="Pick every ticket" />
            {chosen.length ? `${chosen.length} picked` : `Pick all ${pickable.length}`}
          </label>
          <span className="grow" />
          {chosen.length ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setPicked([])}>Clear</Button>
              <Button size="sm" icon="edit" onClick={() => setEditingMany(true)}>Edit {chosen.length}</Button>
              <Button size="sm" variant="danger" icon={can('repair.cancel') ? 'close' : 'lock'} onClick={() => setCancelling(true)} title={can('repair.cancel') ? undefined : 'Needs the owner’s PIN'}>
                Cancel {chosen.length} {jobKind === 'vehicle' ? 'work order' : 'repair'}{chosen.length === 1 ? '' : 's'}
              </Button>
              {toReady.length ? <Button size="sm" variant="primary" icon="check" busy={busy} onClick={() => setConfirming(true)}>Mark {toReady.length} ready</Button> : null}
            </>
          ) : <span className="small muted">{jobKind === 'vehicle' ? 'Tick work orders to mark them ready or cancel them together' : 'Tick tickets to mark them ready or cancel them together'}</span>}
        </div>
      ) : null}
      {list.data?.repairs.length ? (
        <div className="list">
          {list.data.repairs.map((r) => (
            <div key={r.id} className="pc-row">
            {picking && pickable.length ? (
              <label className="pc-pick" aria-label={`Pick ${r.ticket}`}>
                {isOpen(r) ? <input type="checkbox" checked={picked.includes(r.id)} onChange={() => toggle(r.id)} /> : null}
              </label>
            ) : null}
            <a className="list-row" href={`#/repairs/${r.id}`}>
              {/* A work order or job ticket (a vehicle, a vacuum, a computer): no phone picture. */}
              {r.job_kind ? <span className="thumb job-thumb" aria-hidden="true"><Icon.wrench /></span> : <span className="thumb"><ModelPhoto brand={r.brand} model={r.model} color={r.color} size={56} /></span>}
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{r.job_kind ? r.device_name : `${r.brand} ${r.model}`} · {r.customer_name}</span>
                <span className="s" style={{ display: 'block' }}>{r.ticket}{r.plate ? ` · ${r.plate}` : ''} · {r.services || r.issue}{many && location && r.location_name ? ` · ${r.location_name}` : ''}</span>
                <span className="s" style={{ display: 'block' }}>
                  {r.status === 'ready' && r.ready_at ? `Ready since ${when(r.ready_at)}` : r.promised_at && OPEN_REPAIR_STATUSES.includes(r.status) ? <span className={r.overdue ? 'overdue' : ''}>{r.overdue ? 'Late · was due ' : 'Ready by '}{when(r.promised_at)}</span> : `Checked in ${when(r.created_at)}`}
                  {r.technician_name ? ` · ${r.technician_name}` : ''}
                </span>
              </span>
              <span className="stack-sm" style={{ alignItems: 'flex-end' }}>
                <StatusPill status={r.status} />
                <span className="row" style={{ gap: 6 }}>
                  {r.pickup_on_pay ? <Badge tone="blue" icon="user">Customer here</Badge> : null}
                  {r.paid ? <Badge tone="green" icon="check">Paid</Badge> : null}<Money cents={r.price_cents} className="strong" />
                </span>
                {r.cost_cents !== null && r.cost_cents !== undefined ? <span className="pill-row" style={{ justifyContent: 'flex-end' }}><Cost cents={r.cost_cents} /><Profit cents={r.profit_cents} /></span> : null}
              </span>
            </a>
            </div>
          ))}
        </div>
      ) : list.data ? (
        <Empty icon="wrench" title={q ? `No repairs match "${q}".` : bench ? (bench === 'me' ? 'Nothing on your bench here.' : 'Nothing on their bench here.') : status === 'open' ? 'No open repairs.' : status === 'today' ? 'No tickets made today yet.' : date ? `No tickets on ${longDate(date)}.` : 'Nothing here.'}>
          {bench ? <Button variant="ghost" onClick={() => pickBench('')}>Show everyone’s tickets</Button>
            : can('repair.create') && status === 'open' && !q ? <Button variant="primary" icon="plus" onClick={() => navigate('/repairs/new')}>{checkInLabel}</Button> : null}
        </Empty>
      ) : null}
      {editingMany ? (
        <BulkEditSheet
          title={jobKind === 'vehicle' ? 'Edit work orders' : 'Edit repairs'}
          what={jobKind === 'vehicle' ? 'work orders' : 'tickets'}
          items={chosen.map((r) => ({ id: r.id, label: `${r.ticket} · ${r.job_kind ? r.device_name : `${r.brand} ${r.model}`} · ${r.customer_name}` }))}
          fields={bulkFields}
          apply={applyBulk}
          onClose={() => setEditingMany(false)}
          onDone={() => { setEditingMany(false); setPicked([]); list.reload(); }}
        />
      ) : null}
      {closingOld && oldRd.data ? (
        <CloseOldRepairDeskSheet
          data={oldRd.data}
          location={location}
          onClose={() => setClosingOld(false)}
          onDone={() => { setClosingOld(false); oldRd.reload(); list.reload(); }}
        />
      ) : null}
      {confirming ? (
        <Confirm title={`Mark ${toReady.length} ticket${toReady.length === 1 ? '' : 's'} ready?`} confirmLabel={`Mark ${toReady.length} ready`} busy={busy} onClose={() => setConfirming(false)} onConfirm={markReady}>
          <p>{toReady.slice(0, 8).map((r) => r.ticket).join(', ')}{toReady.length > 8 ? ` and ${toReady.length - 8} more` : ''} move to <strong>Ready for pickup</strong>.</p>
          {chosen.length > toReady.length ? <p className="small muted">{chosen.length - toReady.length} picked {chosen.length - toReady.length === 1 ? 'is' : 'are'} already ready and stay{chosen.length - toReady.length === 1 ? 's' : ''} that way.</p> : null}
          <p className="small muted">Customers who asked for text updates get the “ready” text, like when you mark one ready.</p>
        </Confirm>
      ) : null}
      {cancelling ? (
        <CancelRepairsSheet
          tickets={chosen.map((r) => ({ id: r.id, ticket: r.ticket, paid: !!r.paid, name: `${r.brand} ${r.model} · ${r.customer_name}` }))}
          onClose={() => setCancelling(false)}
          onDone={() => { setCancelling(false); setPicked([]); list.reload(); }}
        />
      ) : null}
    </div>
  );
}

/**
 * The owner's "Close old RepairDesk tickets": the tickets brought over from RepairDesk that nobody
 * has touched here in the days picked close as Picked up with no sale, each with the note "Closed:
 * handled in RepairDesk". Ones paid or worked on in STORE OS stay open.
 */
function CloseOldRepairDeskSheet({ data, location, onClose, onDone }: { data: OldRepairDesk; location: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const { run, busy } = useAction();
  const [days, setDays] = useState(30);
  const n = data.ages.find((a) => a.days === days)?.count ?? 0;
  const close = async () => {
    const res = await run(() => post<{ closed: number }>('/api/repairs/repairdesk/close', { days, location: location || null }));
    if (!res) return;
    toast(res.closed ? `${res.closed} old RepairDesk ticket${res.closed === 1 ? '' : 's'} closed.` : 'Nothing to close.', res.closed ? 'good' : 'info');
    onDone();
  };
  return (
    <Sheet
      title="Close old RepairDesk tickets"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Keep them</Button><Button variant="primary" busy={busy} disabled={!n} onClick={close}>{n ? `Close ${n} ticket${n === 1 ? '' : 's'}` : 'None to close'}</Button></>}
    >
      <p style={{ marginTop: 0 }}>
        <strong>{data.open.toLocaleString('en-US')}</strong> ticket{data.open === 1 ? '' : 's'} brought over from RepairDesk {data.open === 1 ? 'is' : 'are'} still open.
      </p>
      <div className="stack-sm">
        <span className="small strong">Close the ones nobody has touched in</span>
        <div className="chips" aria-label="How long untouched">
          {data.ages.map((a) => (
            <button key={a.days} type="button" className="chip" aria-pressed={days === a.days} onClick={() => setDays(a.days)}>
              {a.days} days <span className={days === a.days ? '' : 'muted'}>{a.count}</span>
            </button>
          ))}
        </div>
      </div>
      <p className="small muted">Counted from each ticket’s last date in RepairDesk. They close as Picked up, with no sale, and each one gets the note “Closed: handled in RepairDesk”.</p>
      {data.worked ? <p className="small muted">{data.worked} paid or worked on here in STORE OS stay{data.worked === 1 ? 's' : ''} open.</p> : null}
    </Sheet>
  );
}

// ---------------------------------------------------------------- check-in

interface DraftLine {
  key: string;
  description: string;
  priceCents: number;
  hubOptionId?: number;
  warranty?: string;
  from?: boolean;
  call?: boolean;
  /**
   * The part used, from a supplier's site (PHORA, PhoneLCDParts, Kracked Screens, MobileSentrix).
   * It never changes the price: its value is the taxed parts amount, and the server looks up what
   * it cost.
   */
  partKey?: string;
  supplier?: SupplierId | null;
  supplierSku?: string | null;
  supplierUrl?: string | null;
  partName?: string | null;
  partImage?: string | null;
  /** The part's retail value (taxed); without a part, the store's usual share of the price is. */
  partsCents?: number | null;
  /** Added just for a part when no repair was picked yet: it needs its price. */
  fromPart?: boolean;
  /** A repair price to suggest for it (when the website has none). */
  suggestedCents?: number | null;
  /** The website's estimated time for it ("30–60 min"): it picks the repair time. */
  time?: string;
  /** A part from this store's stock: it comes out of inventory when the ticket is made. */
  productId?: string | null;
  productName?: string | null;
}

/** The parts amount taxed on a line: the part's value, or the store's usual share of the price. */
const lineParts = (l: DraftLine, share: number) => (l.partsCents != null ? Math.min(Math.max(l.partsCents, 0), l.priceCents) : defaultPartsCents(l.description, l.priceCents, share));

const supplierLabel = (id: SupplierId | null | undefined) => SUPPLIERS.find((x) => x.id === id)?.name ?? 'a supplier';

/** The model as the suppliers' search wants it: "iPad Pro 11 (4th Gen)" is "iPad Pro 11 4th Gen". */
const partsQueryModel = (model: string) => model.replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();


/** Pictures for the menu tiles. */
const TILE_ART: Record<DeviceType, { brand?: string; model?: string; category?: string }> = {
  iphone: { brand: 'Apple', model: 'iPhone 16 Pro' },
  phone: { brand: 'Samsung', model: 'Galaxy S25' },
  ipad: { brand: 'Apple', model: 'iPad Pro' },
  macbook: { brand: 'Apple', model: 'MacBook Pro' },
  console: { model: 'PlayStation 5', category: 'consoles' },
  desktop: { category: 'desktop' },
  car: { category: 'car' },
};
/** Typed in (no model list): what goes in the brand field. */
const TYPED_BRAND: Partial<Record<DeviceType, string>> = { desktop: 'Desktop', car: 'Car part' };

export function RepairCheckInScreen({ query }: { query: URLSearchParams }) {
  const { me, status, can } = useSession();
  // Texting, the suppliers' parts search and the unlock sites run on West Coast Wireless's own
  // accounts: its store only, for now.
  const own = usePlan().home;
  const toast = useToast();
  const { run, busy } = useAction();
  // UNLOCK on the home screen opens this in service mode: unlocks, resets, software, data transfer.
  const serviceMode = query.get('service') === '1';
  const catalog = useLoad<any>(() => (serviceMode ? get('/api/device-catalog') : Promise.resolve(null)), [serviceMode]);
  const startType = query.get('type');
  const [type, setType] = useState<DeviceType | null>(isDeviceType(startType) ? startType : null);
  const [picked, setPicked] = useState(false);
  const [customer, setCustomer] = useState<CartCustomer | null>(null);
  const [brand, setBrand] = useState('Apple');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [code, setCode] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [issue, setIssue] = useState('');
  const [pre, setPre] = useState<Record<string, PrecheckValue>>({});
  const [condition, setCondition] = useState('');
  const [leftWith, setLeftWith] = useState<string[]>([]);
  const [consent, setConsent] = useState(false);
  const [passcode, setPasscode] = useState('');
  // The repair time, picked at the bottom: 25, 35 or 60 minutes (or longer). Until someone picks
  // one, it follows the website's estimated time for the prices picked.
  const [minutes, setMinutes] = useState<number | null>(null);
  const [longer, setLonger] = useState(false);
  const [timeByHand, setTimeByHand] = useState(false);
  const pickMinutes = (m: number) => { setMinutes(m); setTimeByHand(true); };
  const [tech, setTech] = useState('');
  // The texting box: unticked until the customer says yes (a yes already on their record is kept).
  const [notify, setNotify] = useState(false);
  const [texting, setTexting] = useState<{ agreed: boolean; opted_out: boolean; text: string } | null>(null);
  useEffect(() => {
    if (!customer?.id || !customer.phone) { setTexting(null); setNotify(false); return; }
    let live = true;
    get(`/api/customers/${customer.id}/texting`).then((t) => { if (live) { setTexting(t); setNotify(!!t.agreed && !t.opted_out); } }).catch(() => undefined);
    return () => { live = false; };
  }, [customer?.id, customer?.phone]);
  const [custom, setCustom] = useState(false);
  // The line picking a part from stock (its key), while the parts sheet is open.
  const [stockFor, setStockFor] = useState<string | null>(null);
  const services = useMemo(() => (me?.settings?.service_menu ?? '').split('\n').map((line) => {
    const [name, price] = line.split('|').map((x) => x.trim());
    return name && price ? { name, priceCents: Math.round(Number(price) * 100) } : null;
  }).filter((x): x is { name: string; priceCents: number } => !!x && Number.isFinite(x.priceCents)), [me?.settings?.service_menu]);
  const hasModels = !!type && MENU_TYPES_WITH_MODELS.includes(type);
  const options = useRepairOptions(serviceMode || !hasModels || !picked ? '' : brand, serviceMode || !hasModels || !picked ? '' : model);

  useEffect(() => {
    const id = query.get('customer');
    if (!id) return;
    get(`/api/customers/${id}`).then((c) => setCustomer({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents })).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (customer) setNotify(!!customer.phone); }, [customer]);

  // Warranty re-check (from a picked-up ticket): the same customer and device, linked to that ticket.
  const [warranty, setWarranty] = useState<{ id: string; ticket: string; device: string; work: string; closed_at: number | null } | null>(null);
  useEffect(() => {
    const id = query.get('warranty');
    if (!id) return;
    get(`/api/repairs/${id}`).then(async (r) => {
      setWarranty({ id: r.id, ticket: r.ticket, device: r.device_name, work: r.lines.map((l: any) => l.description).join(', ') || r.issue, closed_at: r.closed_at });
      setType(isDeviceType(r.device_type) ? r.device_type : deviceTypeOf(r.brand, r.model));
      setBrand(r.brand);
      setModel(r.model);
      setPicked(true);
      setColor(r.color ?? '');
      setCode(r.imei ?? r.serial ?? '');
      const c = await get(`/api/customers/${r.customer.id}`).catch(() => null);
      setCustomer(c
        ? { id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents }
        : { id: r.customer.id, name: r.customer.name, phone: r.customer.phone, email: r.customer.email, store_credit_cents: 0 });
    }).catch((e) => toast(e instanceof Error ? e.message : 'The first ticket couldn’t be loaded.', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The website's time for the prices picked (the longest), used until a time is picked by hand.
  const websiteMinutes = useMemo(() => {
    const times = lines.map((l) => repairMinutesFromEstimate(l.time)).filter((m): m is number => m !== null);
    return times.length ? Math.max(...times) : null;
  }, [lines]);
  useEffect(() => {
    if (timeByHand) return;
    setMinutes(websiteMinutes);
    if (websiteMinutes !== null && !(REPAIR_TIMES as readonly number[]).includes(websiteMinutes)) setLonger(true);
  }, [websiteMinutes, timeByHand]);

  const brands: string[] = useMemo(() => {
    const all: string[] = catalog.data?.brands ?? ['Apple', 'Samsung', 'Google'];
    const top = ['Apple', 'Samsung', 'Google', 'Motorola'].filter((b) => all.includes(b));
    return [...top, ...all.filter((b) => !top.includes(b))];
  }, [catalog.data]);
  const models: string[] = catalog.data?.models?.[brand] ?? [];
  const suggestions = useMemo(() => {
    const t = model.trim().toLowerCase();
    const exact = models.some((m) => m.toLowerCase() === t);
    if (exact) return [];
    return models.filter((m) => !t || m.toLowerCase().includes(t)).sort((a, b) => b.localeCompare(a, undefined, { numeric: true })).slice(0, 14);
  }, [model, models]);

  const pickType = (t: DeviceType) => {
    setType(t);
    setPicked(false);
    setModel('');
    setLines([]);
    setPre({});
    setLeftWith([]);
    setBrand(TYPED_BRAND[t] ?? (t === 'phone' ? 'Samsung' : t === 'console' ? '' : 'Apple'));
  };
  const pickModel = (b: string, m: string) => {
    setBrand(b);
    setModel(m);
    setPicked(true);
    setLines([]);
  };

  const digits = code.replace(/\D/g, '');
  const isImei = digits.length === 15 && digits === code.trim();
  const imeiBad = isImei && !isValidImei(digits);
  const share = Number(me?.settings.repair_parts_share_pct ?? 50);
  const rate = me?.location?.tax_rate_ppm ?? 0;
  const estimate = useMemo(() => {
    const parts = repairCheckoutParts(lines.map((l) => ({ description: l.description, price_cents: l.priceCents, parts_cents: lineParts(l, share) })));
    const calc = calculateCart(parts.map((p) => ({ qty: 1, unitPriceCents: p.priceCents, taxable: p.taxable })), 0, rate);
    return { ...calc, partsCents: lines.reduce((s, l) => s + lineParts(l, share), 0) };
  }, [lines, share, rate]);

  const toggleOption = (r: HubRepair, o: HubOption) => {
    if (!issue.trim()) setIssue(r.description);
    if (lines.some((l) => l.hubOptionId === o.id)) return setLines(lines.filter((l) => l.hubOptionId !== o.id));
    const line: DraftLine = { key: `h${o.id}`, description: o.label, priceCents: o.price_cents ?? 0, hubOptionId: o.id, warranty: o.warranty, from: o.from, call: o.price_cents === null, time: o.time };
    // A part picked first waits on its own line for a price: the website price takes that line over.
    const g = repairLineGroup(o.label) ?? repairLineGroup(r.type);
    const waiting = lines.find((l) => l.fromPart && !l.priceCents && (!g || repairLineGroup(l.description) === g));
    setLines(waiting ? lines.map((l) => (l.key === waiting.key ? { ...waiting, ...line, fromPart: false, suggestedCents: null } : l)) : [...lines, line]);
  };
  /**
   * The part a repair uses: it goes on the repair it's for (the screen on the screen repair), or
   * the first repair without a part. It never changes the price; only its sales tax is added.
   * Tapping it again takes it off. With no repair picked yet, it starts one that needs a price.
   */
  const togglePart = (p: RepairPart) => {
    const key = repairPartKey(p);
    const on = lines.find((l) => l.partKey === key);
    if (on) {
      setLines(on.fromPart ? lines.filter((l) => l.key !== on.key) : lines.map((l) => (l.key === on.key ? { ...l, partKey: undefined, supplier: null, supplierSku: null, supplierUrl: null, partName: null, partImage: null, partsCents: undefined } : l)));
      return;
    }
    const part = { partKey: key, supplier: p.supplier, supplierSku: p.sku, supplierUrl: p.url, partName: p.name, partImage: p.image, partsCents: p.price_cents ?? null };
    const target = lines.find((l) => repairLineGroup(l.description) === p.group && !l.partKey) ?? lines.find((l) => !l.partKey && !l.key.startsWith('s')) ?? null;
    if (target) {
      setLines(lines.map((l) => (l.key === target.key ? { ...l, ...part } : l)));
      toast(`${p.name} is on ${target.description}. The price stays ${formatCents(target.priceCents)}; only its sales tax is added.`, 'good');
      return;
    }
    setLines([...lines, { key: `p${Date.now()}`, description: REPAIR_WORDS[p.group as PartGroup] ?? 'Repair', priceCents: 0, call: true, fromPart: true, suggestedCents: p.suggested_cents, ...part }]);
  };

  const techs = status?.device?.employees ?? [];
  const needsPrice = lines.some((l) => l.call && !l.priceCents);
  // Every priced repair says what its part is worth (taxed) before the ticket is made: the sales
  // tax is on the part, so nothing is guessed. Diagnostics, software and unlocks have no part.
  const lineNeedsParts = (l: DraftLine) => l.priceCents > 0 && !isLaborOnly(l.description) && l.partsCents == null;
  const needsParts = lines.find(lineNeedsParts);
  const missing = !customer ? 'the customer' : !model.trim() ? 'the model' : !(issue.trim() || lines.length) ? 'the repair' : needsPrice ? 'a price' : needsParts ? `the part's value (taxed) on ${needsParts.description}` : minutes === null ? 'the repair time' : null;
  const canSave = !missing && !imeiBad && !busy;
  const kind: DeviceType = serviceMode ? deviceTypeOf(brand, model) : type ?? 'phone';
  const checks = serviceMode ? PRECHECK_ITEMS : precheckItemsFor(kind);
  const leftOptions = serviceMode ? LEFT_WITH_OPTIONS : LEFT_WITH_BY_TYPE[kind];

  const save = async () => {
    if (!customer) return toast('Pick the customer first.', 'error');
    const body = {
      customerId: customer.id, brand: brand.trim() || 'Other', model: model.trim(), color: color.trim() || null,
      imei: isImei ? digits : null, serial: !isImei && code.trim() ? code.trim() : null,
      passcode: consent ? passcode : null, passcodeConsent: consent,
      issue: issue.trim() || lines.map((l) => l.description).join(', '),
      conditionNotes: condition.trim() || null, precheck: pre, leftWith,
      technicianId: tech || null, repairMinutes: minutes, promisedAt: minutes ? Date.now() + minutes * 60_000 : null, notifySms: own && notify,
      deviceType: serviceMode ? null : type, warrantyOf: warranty?.id ?? null,
      lines: lines.map((l) => ({
        description: l.description, priceCents: l.priceCents, hubOptionId: l.hubOptionId ?? null, warranty: l.warranty ?? null,
        // The taxed parts value typed (or the part's); a labor-only service sends none and gets none.
        partsCents: l.partsCents ?? (isLaborOnly(l.description) ? 0 : null),
        ...(l.supplier ? { supplier: l.supplier, supplierSku: l.supplierSku ?? null, supplierUrl: l.supplierUrl ?? null, partName: l.partName ?? null } : {}),
        // A part from stock comes out of inventory with the ticket.
        ...(l.productId ? { productId: l.productId } : {}),
      })),
    };
    const r = await run(() => post('/api/repairs', body));
    if (r) {
      toast(`${r.ticket} checked in.`, 'good');
      // The ticket opens with "Print Technician Label" and "Print Thermal Receipt" right there.
      navigate(`/repairs/${r.id}?created=1`);
    }
  };

  // ---- the cards, in the order they show

  const customerCard = (n: number) => (
    <section className="card stack" key="customer">
      <h2>{n}. Customer</h2>
      <CustomerSearch value={customer} onChange={setCustomer} />
    </section>
  );

  const deviceFields = (
    <div className="form-grid">
      <Field label="Color" hint="optional"><input className="input" value={color} onChange={(e) => setColor(e.target.value)} placeholder="e.g. Midnight" /></Field>
      <Field label="IMEI or serial number" hint={imeiBad ? 'That IMEI doesn’t add up. Dial *#06# to check.' : 'scan or type; optional'}>
        <div className="imei-row">
          <input className="input grow" value={code} onChange={(e) => setCode(e.target.value)} inputMode="text" aria-invalid={imeiBad} placeholder="*#06# shows it" aria-label="IMEI or serial number" />
          <PhoneCheckButton code={code} />
        </div>
      </Field>
    </div>
  );

  // Service mode keeps the quick brand and model boxes.
  const serviceDeviceCard = (n: number) => (
    <section className="card stack" key="device">
      <h2>{n}. Device</h2>
      <div className="chips" aria-label="Brand">
        {brands.slice(0, 10).map((b) => (
          <button key={b} type="button" className="chip" aria-pressed={brand === b} onClick={() => { setBrand(b); setModel(''); setLines([]); }}>{b}</button>
        ))}
      </div>
      <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
        <span className="thumb" style={{ width: 80, height: 80 }}><ModelPhoto brand={brand} model={model} color={color} size={80} /></span>
        <div className="grow stack-sm">
          <Field label="Model"><input className="input input-lg" value={model} onChange={(e) => setModel(e.target.value)} placeholder={brand === 'Apple' ? 'e.g. iPhone 13' : 'Type the model'} aria-label="Model" /></Field>
          {suggestions.length ? (
            <div className="chips" aria-label="Models">
              {suggestions.map((m) => <button key={m} type="button" className="chip" onClick={() => setModel(m)}>{m}</button>)}
            </div>
          ) : null}
        </div>
      </div>
      {deviceFields}
      {own ? <UnlockTools code={code} /> : null}
    </section>
  );

  // The menu: what's being fixed, then the model.
  const menuCard = (n: number) => (
    <section className="card stack" key="device">
      <div className="spread">
        <h2>{n}. What are we fixing?{type ? <span className="muted" style={{ fontWeight: 600 }}> · {DEVICE_TYPE_LABELS[type]}</span> : null}</h2>
        {type ? <Button size="sm" variant="ghost" icon="chevronLeft" onClick={() => { setType(null); setPicked(false); setModel(''); setLines([]); }}>Something else</Button> : null}
      </div>
      {!type ? (
        <div className="fix-grid" role="list" aria-label="What are we fixing">
          {REPAIR_MENU.map((t) => (
            <button key={t.type} type="button" role="listitem" className="fix-tile" onClick={() => pickType(t.type)} aria-label={t.label}>
              <PhotoTile src={REPAIR_TYPE_PHOTOS[t.type]} alt="" size={72} fallback={<DeviceArt {...TILE_ART[t.type]} size={60} />} />
              <span className="t">{t.label}</span>
              <span className="s">{t.sub}</span>
            </button>
          ))}
        </div>
      ) : hasModels && !picked ? (
        <ModelMenu type={type} onPick={pickModel} />
      ) : hasModels ? (
        <>
          <div className="row" style={{ gap: 16, alignItems: 'center' }}>
            <span className="thumb" style={{ width: 72, height: 72 }}><ModelPhoto brand={brand} model={model} color={color} size={72} /></span>
            <div className="grow">
              <div className="small muted">{DEVICE_TYPE_LABELS[type]}</div>
              <div className="strong" style={{ fontSize: 20 }}>{repairDeviceName(brand, model)}</div>
            </div>
            <Button size="sm" onClick={() => { setPicked(false); setLines([]); }}>Change model</Button>
          </div>
          {deviceFields}
        </>
      ) : (
        <>
          <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
            <span className="thumb" style={{ width: 72, height: 72 }}><Photo src={REPAIR_TYPE_PHOTOS[type]} alt="" size={72} fallback={<DeviceArt {...TILE_ART[type]} size={72} />} /></span>
            <div className="grow">
              <Field label={type === 'car' ? 'Which car part' : 'Which computer'}>
                <input className="input input-lg" value={model} onChange={(e) => setModel(e.target.value)} placeholder={type === 'car' ? 'e.g. 2019 Honda Civic radio screen' : 'e.g. HP Pavilion desktop'} aria-label="Model" />
              </Field>
            </div>
          </div>
          {deviceFields}
        </>
      )}
    </section>
  );

  const setLine = (key: string, patch: Partial<DraftLine>) => setLines(lines.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const linesList = lines.length ? (
    <div className="list">
      {lines.map((l) => {
        const parts = lineParts(l, share);
        return (
          <div key={l.key} className="list-row repair-line" style={{ cursor: 'default' }}>
            <span className="thumb" style={{ width: 48, height: 48 }}><Photo src={l.partImage || repairPhotoFor(l.description, type)} alt="" size={48} /></span>
            <span className="main-col">
              <span className="t" style={{ display: 'block' }}>{l.description}</span>
              {l.call && !l.priceCents ? (
                <span className="s" style={{ display: 'block', color: 'var(--amber-ink, #92400e)' }}>
                  {l.fromPart ? 'Enter the repair price (the part is already in it)' : 'Website says "call for price": enter the price you quoted'}
                  {l.suggestedCents ? <> · <button type="button" className="link-btn" onClick={() => setLine(l.key, { priceCents: l.suggestedCents ?? 0 })}>Use {formatCents(l.suggestedCents)}</button></> : null}
                </span>
              ) : l.warranty ? <span className="s" style={{ display: 'block' }}>{l.warranty}</span> : null}
              {l.productId ? (
                <span className="s part-used" style={{ display: 'block' }}>
                  <Icon.check /> Part from stock: {l.productName} · comes out of inventory with the ticket · part value {formatCents(parts)} taxed
                  {' · '}<button type="button" className="link-btn" onClick={() => setLine(l.key, { productId: null, productName: null, partsCents: undefined })}>Put it back</button>
                </span>
              ) : l.partName ? (
                <span className="s part-used" style={{ display: 'block' }}>
                  <Icon.check /> Part: {l.partName} ({supplierLabel(l.supplier)}) · part value {formatCents(parts)} taxed · price unchanged
                </span>
              ) : lineNeedsParts(l) ? (
                <span className="s" style={{ display: 'block', color: 'var(--amber-ink, #92400e)' }}>
                  <button type="button" className="link-btn" onClick={() => setStockFor(l.key)}>Part from stock</button>
                  {' · '}Type the part's value (taxed) · <button type="button" className="link-btn" onClick={() => setLine(l.key, { partsCents: defaultPartsCents(l.description, l.priceCents, share) })}>Usual share {formatCents(defaultPartsCents(l.description, l.priceCents, share))}</button>
                  {' · '}<button type="button" className="link-btn" onClick={() => setLine(l.key, { partsCents: 0 })}>No part (labor only)</button>
                </span>
              ) : l.priceCents ? <span className="s" style={{ display: 'block' }}>{parts ? `Taxed parts ${formatCents(parts)} · labor ${formatCents(l.priceCents - parts)}` : 'Labor only (no tax)'}{!isLaborOnly(l.description) ? <> · <button type="button" className="link-btn" onClick={() => setStockFor(l.key)}>Part from stock</button></> : null}</span> : null}
            </span>
            <span className="stack-sm" style={{ width: 150, gap: 4 }}>
              <span className="tiny muted">Price</span>
              <MoneyInput value={l.priceCents} onChange={(v) => setLine(l.key, { priceCents: v ?? 0 })} ariaLabel={`Price for ${l.description}`} />
              {l.priceCents > 0 && !isLaborOnly(l.description) ? (
                <>
                  <span className="tiny muted">Parts (taxed)</span>
                  <MoneyInput value={l.partsCents ?? null} onChange={(v) => setLine(l.key, { partsCents: v })} ariaLabel={`Taxed parts value for ${l.description}`} />
                </>
              ) : null}
            </span>
            <IconButtonSmall label={`Remove ${l.description}`} onClick={() => setLines(lines.filter((x) => x.key !== l.key))} />
          </div>
        );
      })}
      <div className="repair-totals" aria-label="Estimate">
        <div className="rt-row"><span>Repair price</span><span className="num">{formatCents(estimate.subtotal)}</span></div>
        <div className="rt-row"><span>Sales tax {formatRatePpm(rate)} <span className="muted">on {formatCents(estimate.partsCents)} of parts</span></span><span className="num">{formatCents(estimate.tax)}</span></div>
        <div className="rt-row rt-total"><span>Total</span><span className="num">{formatCents(estimate.total)}</span></div>
        <div className="small muted">The part is already in the repair price. California taxes the part only, not the labor, so every priced repair says what its part is worth before the ticket is made.</div>
      </div>
    </div>
  ) : null;

  const websitePrices = options.data?.repairs.length ? (
    <div className="stack-sm">
      <div className="small strong muted" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Website prices</div>
      {options.data.repairs.map((r) => (
        <div key={r.type} className="stack-sm">
          <div className="svc-head"><Photo src={repairPhotoFor(r.type, type)} alt="" size={40} className="svc-pic" /><span className="small strong">{r.type}</span></div>
          <div className="svc-grid">
            {r.options.map((o) => (
              <button key={o.id} type="button" className="svc" aria-pressed={lines.some((l) => l.hubOptionId === o.id)} onClick={() => toggleOption(r, o)}>
                <span className="t">{o.option || r.type}</span>
                <span className="s">{[o.time, o.warranty].filter(Boolean).join(' · ')}</span>
                <span className="p">{o.price_cents === null ? 'Call for price' : `${o.from ? 'From ' : ''}${formatCents(o.price_cents)}`}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  ) : null;

  const fixCard = (n: number) => (
    <section className="card stack" key="fix">
      <div className="spread"><h2>{n}. {serviceMode ? 'Service' : 'Repairs and prices'}</h2><Button size="sm" icon="plus" onClick={() => setCustom(true)}>{serviceMode ? 'Other service' : 'Add a new item'}</Button></div>
      {serviceMode ? (
        <div className="svc-grid">
          {services.map((sv) => (
            <button key={sv.name} type="button" className="svc" aria-pressed={lines.some((l) => l.key === `s${sv.name}`)} onClick={() => {
              if (lines.some((l) => l.key === `s${sv.name}`)) setLines(lines.filter((l) => l.key !== `s${sv.name}`));
              else { setLines([...lines, { key: `s${sv.name}`, description: sv.name, priceCents: sv.priceCents }]); if (!issue.trim()) setIssue(sv.name); }
            }}>
              <span className="t">{sv.name}</span>
              <span className="s">labor only, not taxed</span>
              <span className="p">{formatCents(sv.priceCents)}</span>
            </button>
          ))}
        </div>
      ) : !type ? <p className="muted">Pick what you’re fixing above.</p> : hasModels && !picked ? <p className="muted">Pick the model to see your prices.</p> : null}
      {!serviceMode && hasModels && picked ? (
        <>
          {options.loading && !options.data ? <Spinner label="Getting website prices" /> : websitePrices}
          {options.data && !options.data.repairs.length ? (
            options.data.source === 'none' ? (
              <div className="row" style={{ gap: 12 }}>
                <p className="small muted grow" style={{ margin: 0 }}>The website’s prices didn’t load. Its price list may be waking up; try again in a minute.</p>
                <Button size="sm" icon="refresh" onClick={() => options.reload()}>Try again</Button>
              </div>
            ) : <p className="small muted">The website has no prices for this model. Use the parts below or “Add a new item”.</p>
          ) : null}
          {own ? <RepairPartsSearch model={partsQueryModel(model)} chosen={lines.map((l) => l.partKey ?? '')} onPick={togglePart} showCost={can('inventory.view_cost')} startWord={partWord(lines)} /> : null}
        </>
      ) : null}
      {!serviceMode && type && !hasModels && model.trim() ? <p className="small muted">Add the repair and your price with “Add a new item”.</p> : null}
      {linesList}
      <Field label="What's wrong, in the customer's words"><textarea className="input" rows={2} value={issue} onChange={(e) => setIssue(e.target.value)} /></Field>
    </section>
  );

  const precheckCard = (n: number) => (
    <section className="card stack" key="precheck">
      <div className="spread">
        <h2>{n}. Drop-off check</h2>
        <Button size="sm" icon="check" onClick={() => setPre(allWork(checks))} title="Sets every check to Works; then change the ones that don’t">All work</Button>
      </div>
      <p className="small muted">Check what works before the repair, so there are no surprises later. “All work” sets every check to ✓; then tap the ones that don’t.</p>
      <PrecheckGrid checks={checks} value={pre} onChange={setPre} />
      <Field label="Scratches, dents or other damage" hint="optional"><input className="input" value={condition} onChange={(e) => setCondition(e.target.value)} /></Field>
      <div className="stack-sm">
        <span className="small strong">Left with the device</span>
        <div className="chips">
          {leftOptions.map((o) => <button key={o} type="button" className="chip" aria-pressed={leftWith.includes(o)} onClick={() => setLeftWith(leftWith.includes(o) ? leftWith.filter((x) => x !== o) : [...leftWith, o])}>{o}</button>)}
        </div>
      </div>
    </section>
  );

  const pickupCard = (n: number) => (
    <section className="card stack" key="pickup">
      <h2>{n}. Passcode and pickup</h2>
      <label className="check"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /> The customer agrees to share the passcode so we can test the device</label>
      {consent ? <Field label="Passcode" hint="only managers and technicians can see it; erased at pickup"><input className="input" value={passcode} onChange={(e) => setPasscode(e.target.value)} autoComplete="off" /></Field> : <p className="small muted">Without the passcode we can only test what works from the lock screen.</p>}
      {techs.length ? (
        <Field label="Technician" hint="optional">
          <select className="select" value={tech} onChange={(e) => setTech(e.target.value)}>
            <option value="">Anyone</option>
            {techs.map((t: any) => <option key={t.id} value={t.id}>{t.name} ({t.role})</option>)}
          </select>
        </Field>
      ) : null}
      {own ? (texting?.opted_out ? (
        <div className="banner warn"><Icon.alert /><div className="small">{customer?.name} replied STOP to our texts. To get texts again they text START to the store’s number.</div></div>
      ) : (
        <label className="consent-box">
          <input type="checkbox" checked={notify} disabled={!customer?.phone} onChange={(e) => setNotify(e.target.checked)} aria-label="Customer agrees to texts about this repair" />
          <span className="small"><strong>Ask: “Can we text you about this repair, like when it’s ready?”</strong>{customer && !customer.phone ? ' (no phone on file)' : ''}<br /><span className="muted">{texting?.text ?? 'Optional. Msg & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.'}</span></span>
        </label>
      )) : null}
    </section>
  );

  // The repair time, last: 25, 35 or 60 minutes, picked before the ticket is saved.
  const readyAt = (m: number) => formatTime(Date.now() + m * 60_000);
  const timeCard = (n: number) => (
    <section className="card stack" key="time" aria-label="Repair time">
      <div className="spread">
        <h2>{n}. Repair time</h2>
        {minutes ? <span className="small muted">Ready about {readyAt(minutes)}{timeByHand ? '' : ' · from the website’s time, tap another to change it'}</span> : <span className="small strong" style={{ color: 'var(--amber-ink, #92400e)' }}>Pick one</span>}
      </div>
      <div className="time-grid" role="radiogroup" aria-label="Repair time">
        {REPAIR_TIMES.map((m) => (
          <button key={m} type="button" role="radio" aria-checked={minutes === m} className="time-btn" onClick={() => { pickMinutes(m); setLonger(false); }}>
            <span className="tm-n">{m}</span>
            <span className="tm-l">{repairTimeLabel(m)}</span>
            <span className="tm-s">ready about {readyAt(m)}</span>
          </button>
        ))}
      </div>
      {longer || (minutes !== null && !(REPAIR_TIMES as readonly number[]).includes(minutes)) ? (
        <div className="chips" aria-label="Longer repairs">
          {LONGER_REPAIR_TIMES.map((m) => <button key={m} type="button" className="chip" aria-pressed={minutes === m} onClick={() => pickMinutes(m)}>{repairTimeLabel(m)}</button>)}
        </div>
      ) : <button type="button" className="link-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setLonger(true)}>Takes longer?</button>}
    </section>
  );

  // The menu comes first for repairs (what's being fixed, then prices, then who it's for).
  const cards = serviceMode
    ? [customerCard(1), serviceDeviceCard(2), fixCard(3), precheckCard(4), pickupCard(5), timeCard(6)]
    : [menuCard(1), fixCard(2), customerCard(3), precheckCard(4), pickupCard(5), timeCard(6)];

  return (
    <div className="page" style={{ paddingBottom: 120 }}>
      <div className="page-head">
        <h1>{serviceMode ? 'Unlock, reset or software' : 'Check in a repair'}</h1>
        <Button variant="ghost" icon="chevronLeft" onClick={() => navigate('/repairs')}>Repairs</Button>
      </div>

      {warranty ? (
        <div className="banner">
          <Icon.history />
          <div>
            <div className="strong">Warranty re-check for {warranty.ticket}</div>
            <div className="small">{warranty.device} · {warranty.work}{warranty.closed_at ? ` · picked up ${formatDateTime(warranty.closed_at)}` : ''}. The new ticket is linked to it.</div>
          </div>
        </div>
      ) : null}

      {cards}

      <div className="sticky-actions">
        <span className="grow small muted">{missing ? `Still needed: ${missing}` : `${customer?.name} · ${repairDeviceName(brand, model)} · ${repairTimeLabel(minutes)}${lines.length ? ` · ${formatCents(estimate.total)} with tax` : ''}`}</span>
        <Button size="lg" variant="primary" icon="check" busy={busy} disabled={!canSave} onClick={save}>Check in</Button>
      </div>

      {custom ? <CustomLineSheet title={serviceMode ? 'Other service' : 'Add a new item'} onClose={() => setCustom(false)} onAdd={(d, p) => { setLines([...lines, { key: `c${Date.now()}`, description: d, priceCents: p }]); setCustom(false); }} /> : null}
      {stockFor ? (
        <StockPartSheet
          model={model}
          line={lines.find((l) => l.key === stockFor)?.description ?? ''}
          onClose={() => setStockFor(null)}
          onPick={(p) => {
            setLine(stockFor, { productId: p.id, productName: p.name, partsCents: p.price_cents ?? undefined, partName: null, supplier: null, supplierSku: null, supplierUrl: null, partImage: p.image_url ?? null });
            setStockFor(null);
            toast(`${p.name} is on the ticket. It comes out of stock when the ticket is made.`, 'good');
          }}
        />
      ) : null}
    </div>
  );
}

/** The models for one kind of device: brand tabs, the website's families, a quick find, and "type it". */
function ModelMenu({ type, onPick }: { type: DeviceType; onPick: (brand: string, model: string) => void }) {
  const data = useLoad<{ brands: ModelBrand[]; source: string }>(() => get(`/api/repair-menu/models${qs({ type })}`), [type]);
  const [brandIx, setBrandIx] = useState(0);
  const [find, setFind] = useState('');
  const [typed, setTyped] = useState('');
  const [otherBrand, setOtherBrand] = useState('');
  const list = data.data?.brands ?? [];
  const other = brandIx === list.length && type === 'phone';
  const b = other ? null : list[Math.min(brandIx, Math.max(list.length - 1, 0))] ?? null;
  const f = find.trim().toLowerCase();
  const families = (b?.families ?? [])
    .map((fm) => ({ ...fm, models: fm.models.filter((m) => !f || m.toLowerCase().includes(f) || m.toLowerCase().replace(/\s+/g, '').includes(f.replace(/\s+/g, ''))) }))
    .filter((fm) => fm.models.length);
  const typedBrand = other ? otherBrand.trim() : b?.brand ?? (type === 'phone' ? 'Other' : 'Apple');
  const useTyped = () => onPick(typedBrand || 'Other', typed.trim());
  return (
    <div className="stack">
      {list.length > 1 || type === 'phone' ? (
        <div className="chips" role="tablist" aria-label="Brand">
          {list.map((x, i) => <button key={x.brand} type="button" role="tab" className="chip" aria-selected={i === brandIx} aria-pressed={i === brandIx} onClick={() => setBrandIx(i)}>{x.brand}</button>)}
          {type === 'phone' ? <button type="button" role="tab" className="chip" aria-selected={other} aria-pressed={other} onClick={() => setBrandIx(list.length)}>Other brand</button> : null}
        </div>
      ) : null}
      {!other ? (
        <div className="search">
          <Icon.search />
          <input className="input input-lg" placeholder="Find the model, like 13 Pro or S24" value={find} onChange={(e) => setFind(e.target.value)} aria-label="Find the model" />
        </div>
      ) : null}
      {data.loading && !data.data ? <Spinner label="Loading models" /> : null}
      {!other ? families.map((fm) => (
        <div key={fm.name} className="stack-sm">
          <div className="small strong muted" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>{fm.name}</div>
          <div className="model-grid">
            {fm.models.map((m) => (
              <button key={m} type="button" className="model-btn has-pic" onClick={() => onPick(b!.brand, m)}>
                <span className="mb-pic" aria-hidden="true"><ModelPhoto brand={b!.brand} model={m} category={type === 'console' ? 'consoles' : type === 'ipad' ? 'tablets' : type === 'macbook' ? 'macbooks' : null} size={40} /></span>
                <span>{m}</span>
              </button>
            ))}
          </div>
        </div>
      )) : null}
      {!other && f && !families.length && data.data ? <p className="muted">No model matches “{find}”. Type it below.</p> : null}
      <div className="typed-model">
        {other ? <Field label="Brand"><input className="input" value={otherBrand} onChange={(e) => setOtherBrand(e.target.value)} placeholder="e.g. OnePlus, TCL, Nokia" aria-label="Brand" /></Field> : null}
        <Field label="Not on the list? Type the model">
          <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={type === 'iphone' ? 'e.g. iPhone 18 Pro' : 'Type the model'} aria-label="Type the model" onKeyDown={(e) => { if (e.key === 'Enter' && typed.trim().length >= 2) useTyped(); }} />
        </Field>
        <Button icon="plus" disabled={typed.trim().length < 2 || (other && !otherBrand.trim())} onClick={useTyped}>Use this model</Button>
      </div>
    </div>
  );
}

/** The part to look for first: the part of the first repair picked without one ("battery" for a battery repair). */
function partWord(lines: DraftLine[]): string {
  const g = lines.filter((l) => !l.partKey).map((l) => repairLineGroup(l.description)).find(Boolean);
  return g === 'battery' ? 'battery' : g === 'back' ? 'back glass' : g === 'charging' ? 'charging port' : g === 'camera' ? 'camera' : g === 'audio' ? 'speaker' : 'screen';
}

function IconButtonSmall({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="icon-btn" aria-label={label} onClick={onClick}><Icon.trash /></button>;
}

/**
 * Pick a part from this store's stock for a repair line at check-in: the parts for this model
 * first, or any part by name. It comes out of inventory when the ticket is made, and its price
 * is the line's taxed parts value.
 */
function StockPartSheet({ model, line, onPick, onClose }: { model: string; line: string; onPick: (p: any) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const found = useLoad<any[]>(() => get(`/api/repair-parts${qs({ model, q: dq })}`), [dq]);
  return (
    <Sheet title={`Part from stock${line ? ` for ${line}` : ''}`} onClose={onClose} footer={<Button onClick={onClose}>Cancel</Button>}>
      <p className="small muted">Pick the part you're using. It comes out of this store's stock when the ticket is made, and its cost shows on the profit report.</p>
      <div className="search"><Icon.search /><input className="input" autoFocus placeholder={model ? `Parts for ${model}, or search all parts` : 'Search parts'} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search parts" /></div>
      {found.data?.length ? (
        <div className="list">
          {found.data.map((p: any) => (
            <button key={p.id} type="button" className="list-row" disabled={!(p.qty > 0)} onClick={() => onPick(p)}>
              <span className="thumb"><ProductPicture url={p.image_url} categoryId={p.category_id} name={p.name} size={56} /></span>
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{p.name}</span>
                <span className="s" style={{ display: 'block' }}>{[p.sku, `${p.qty} here`, p.price_cents ? `sells for ${formatCents(p.price_cents)}` : null].filter(Boolean).join(' · ')}{p.cost_cents !== null && p.cost_cents !== undefined ? <> <Cost cents={p.cost_cents} /></> : null}</span>
              </span>
              {p.qty > 0 ? <Badge tone="green">In stock</Badge> : <Badge tone="red">Out</Badge>}
            </button>
          ))}
        </div>
      ) : found.data ? <p className="small muted">No parts found. Add parts in Inventory → Parts, or import your supplier's list.</p> : <Spinner />}
    </Sheet>
  );
}

function CustomLineSheet({ onAdd, onClose, title = 'Add a new item' }: { onAdd: (description: string, priceCents: number) => void; onClose: () => void; title?: string }) {
  const [d, setD] = useState('');
  const [p, setP] = useState<number | null>(null);
  return (
    <Sheet title={title} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!d.trim() || p === null} onClick={() => onAdd(d.trim(), p ?? 0)}>Add</Button></>}>
      <Field label="Repair"><input className="input input-lg" autoFocus value={d} onChange={(e) => setD(e.target.value)} placeholder="e.g. Power button, Face ID, software restore" /></Field>
      <div className="chips">
        {['Diagnostic', 'Software restore', 'Data transfer', 'Power button', 'Volume buttons', 'Vibration motor', 'SIM tray'].map((x) => <button key={x} type="button" className="chip" onClick={() => setD(x)}>{x}</button>)}
      </div>
      <Field label="Price"><MoneyInput value={p} onChange={setP} /></Field>
    </Sheet>
  );
}

// ---------------------------------------------------------------- one repair

const NEXT_STEPS: Partial<Record<RepairStatus, { status: RepairStatus; label: string; primary?: boolean }[]>> = {
  checked_in: [{ status: 'in_progress', label: 'Start repair', primary: true }, { status: 'waiting_parts', label: 'Waiting for parts' }, { status: 'waiting_customer', label: 'Needs customer OK' }, { status: 'ready', label: 'Mark ready' }],
  in_progress: [{ status: 'ready', label: 'Mark ready', primary: true }, { status: 'waiting_parts', label: 'Waiting for parts' }, { status: 'waiting_customer', label: 'Needs customer OK' }],
  waiting_parts: [{ status: 'in_progress', label: 'Parts are here, resume', primary: true }, { status: 'ready', label: 'Mark ready' }],
  waiting_customer: [{ status: 'in_progress', label: 'Customer said yes, resume', primary: true }, { status: 'ready', label: 'Mark ready' }],
  // Something still needs fixing: back on the bench, with no text to the customer.
  ready: [{ status: 'in_progress', label: 'Back to repair' }],
};

/**
 * Prints a repair's tickets. pickup = the customer's receipt (80 mm thermal), tech = the
 * technician's tickets the way the owner picked in Settings → Printers, both = the two, letter =
 * the full page. With the store's printers set up (PrintNode) they print there with no print
 * window; otherwise from this browser.
 */
export function useRepairPrint() {
  const { me } = useSession();
  const toast = useToast();
  const print = usePrint();
  const cloud = useCloudPrint();
  const techFormat = isTechTicketFormat(me?.settings?.tech_ticket_format) ? me!.settings.tech_ticket_format : 'both';
  const printTicket = async (id: string, ticket: string, which: 'both' | 'pickup' | 'tech' | 'letter' = 'both') => {
    const here = (what: 'receipt' | 'tech-receipt' | 'tech-label4' | 'tech-label' | 'letter') => async () => {
      try {
        const p = await get(`/api/repairs/${id}/print`);
        if (what === 'letter') return print(p.job ? <WorkOrderLetter p={p} /> : <RepairTicketLetter p={p} />, 'letter');
        if (what === 'receipt') return print(<RepairPickupPaper p={p} />, 'thermal');
        if (what === 'tech-receipt') return print(<TechTicketReceipt p={p} />, 'thermal');
        if (what === 'tech-label4') return print(<TechTicketTall p={p} />, 'tech-label-4');
        print(<RepairTechLabel p={p} />, 'tech-label');
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Couldn’t print.', 'error');
      }
    };
    if (which === 'letter') return here('letter')();
    if (which !== 'tech') await cloud.printOut({ kind: 'repair_receipt', refId: id, title: `${ticket} receipt`, browser: here('receipt') });
    if (which === 'pickup') return;
    // The technician's tickets, the way the owner picked in Settings → Printers (both: the
    // receipt-paper ticket on the receipt printer and the 4-inch label on the DYMO).
    if (techFormat === 'both' || techFormat === 'receipt') await cloud.printOut({ kind: 'repair_tech_receipt', refId: id, title: `${ticket} tech ticket`, browser: here('tech-receipt'), target: 'receipt' });
    if (techFormat === 'both' || techFormat === 'label-4') await cloud.printOut({ kind: 'repair_tech_label4', refId: id, title: `${ticket} tech label`, browser: here('tech-label4'), target: 'label' });
    if (techFormat === 'label') await cloud.printOut({ kind: 'repair_label', refId: id, title: `${ticket} technician label`, browser: here('tech-label'), target: 'label' });
  };
  return { printTicket, techFormat, cloud };
}

/**
 * On a finished sale that paid for a repair still in the store (paid at drop-off): the tickets
 * printed at check-in said NOT PAID, so they can be printed again here; now they say PAID.
 */
export function SaleRepairPrints({ lines }: { lines: { repair_id?: string | null; kind?: string }[] }) {
  // A deposit's receipt didn't pay for the repair, so its tickets would still say NOT PAID.
  const ids = [...new Set(lines.filter((l) => l.kind !== 'deposit').map((l) => l.repair_id).filter((x): x is string => !!x))];
  const open = useLoad<any[]>(() => Promise.all(ids.map((rid) => get(`/api/repairs/${rid}`).catch(() => null))).then((rs) => rs.filter((r) => r?.open)), [ids.join(',')]);
  const { printTicket } = useRepairPrint();
  if (!open.data?.length) return null;
  return (
    <div className="card card-tight stack-sm" style={{ textAlign: 'left' }}>
      <div className="small muted">Paid before the repair: print the tickets again so they say PAID.</div>
      {open.data.map((r) => (
        <div key={r.id} className="row-wrap">
          <span className="grow strong">{r.ticket} · {r.brand} {r.model}</span>
          <Button icon="printer" size="sm" onClick={() => printTicket(r.id, r.ticket, 'pickup')}>Customer ticket</Button>
          <Button icon="tag" size="sm" onClick={() => printTicket(r.id, r.ticket, 'tech')}>Tech ticket</Button>
          <a className="btn btn-ghost btn-sm" href={`#/repairs/${r.id}`}>Open repair</a>
        </div>
      ))}
    </div>
  );
}

export function RepairDetailScreen({ id, query }: { id: string; query: URLSearchParams }) {
  const { can } = useSession();
  const own = usePlan().home;
  const toast = useToast();
  const { printTicket: printRepair, techFormat, cloud } = useRepairPrint();
  const cart = useCart();
  const { run, busy } = useAction();
  const t = useLoad<any>(() => get(`/api/repairs/${id}`), [id]);
  const [cancel, setCancel] = useState(false);
  const [depositing, setDepositing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editLine, setEditLine] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [passcode, setPasscode] = useState<string | null>(null);
  const [note, setNote] = useState('');
  // "Customer is here" with nothing to pay closes the ticket now: asked once before it does.
  const [handOver, setHandOver] = useState(false);
  // A job ticket's "Customer said yes": the approval sheet, then back to work.
  const [approving, setApproving] = useState(false);
  const printed = useRef(false);
  const r = t.data;

  /** The customer's receipt, the technician's tickets, both, or the full page (see useRepairPrint). */
  const printTicket = (which: 'both' | 'pickup' | 'tech' | 'letter' = 'both') => printRepair(id, t.data?.ticket ?? 'Repair', which);
  const techPaper = techFormat === 'both' ? 'Receipt-paper tech ticket + 4-inch DYMO label' : techFormat === 'receipt' ? 'Receipt paper, 80 mm' : techFormat === 'label-4' ? '4-inch DYMO label' : 'DYMO label, 89 × 29 mm';
  const techKinds: CloudKind[] = techFormat === 'both' ? ['repair_tech_receipt', 'repair_tech_label4'] : techFormat === 'receipt' ? ['repair_tech_receipt'] : techFormat === 'label-4' ? ['repair_tech_label4'] : ['repair_label'];
  // Just checked in: the two print buttons show right away (each can be printed, or both).
  const [printNow, setPrintNow] = useState(() => query.get('created') === '1');
  const [printedNow, setPrintedNow] = useState<string[]>([]);
  useEffect(() => {
    if (r && (query.get('print') === '1' || query.get('created') === '1') && !printed.current) {
      printed.current = true;
      history.replaceState(null, '', `#/repairs/${id}`);
      if (query.get('print') === '1') printTicket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r]);
  const printOne = (which: 'pickup' | 'tech') => { setPrintedNow((x) => [...new Set([...x, which])]); printTicket(which); };
  // One tap: the customer's receipt, then the technician's label.
  const printBoth = () => { setPrintedNow(['pickup', 'tech']); printTicket('both'); };
  const receiptJob = cloud.jobFor('repair_receipt', id);
  const techJobs = techKinds.map((k) => cloud.jobFor(k, id)).filter((j): j is PrintJob => !!j);
  const techSending = techJobs.some((j) => j.state === 'sending');
  const techDone = techJobs.length === techKinds.length && techJobs.every((j) => j.state === 'printed');
  const onPrinter = (kind: 'receipt' | 'label') => (cloud.status?.cloud ? cloud.status[kind]?.name ?? null : null);
  const techPrinters = [techFormat === 'label-4' || techFormat === 'label' ? null : onPrinter('receipt'), techFormat === 'receipt' ? null : onPrinter('label')].filter(Boolean).join(' + ');

  if (t.error) return <div className="page"><Empty icon="alert" title="This repair couldn't be loaded.">{t.error.message}</Empty></div>;
  if (!r) return <div className="page"><Spinner /></div>;

  const work = can('repair.work') && r.open;
  // Paid or closed: a manager can still put the part on a line and fix its taxed value (for the sales tax report).
  const partsOnly = !!r.paid || !r.open;
  const fixParts = partsOnly && can('reports.store') && can('repair.work') && r.status !== 'cancelled';
  const setStatus = async (status: RepairStatus, n?: string) => {
    const res = await run(() => post(`/api/repairs/${id}/status`, { status, note: n ?? null }));
    if (res) {
      t.setData(res);
      if (res.text && own) toast(res.text.message, res.text.status === 'not_configured' ? 'info' : 'good');
    }
  };
  const checkout = async (rep: any = r) => {
    const key = cart.addRepair(rep);
    if (!key) toast('This repair is already in the sale.', 'info');
    if (!cart.cart.customer) {
      const c = await get(`/api/customers/${rep.customer.id}`).catch(() => null);
      if (c) cart.setCustomer({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents, points_balance: c.points_balance, member_code: c.member_code });
    }
    navigate('/sale');
  };
  // "Customer is here": straight to payment, and the ticket closes as picked up once it's paid,
  // whatever its status (no "ready" text). Nothing to pay: it's picked up now.
  const owes = !r.paid && r.totals.total_cents > 0;
  const customerHere = async () => {
    setHandOver(false);
    const res = await run(() => post(`/api/repairs/${id}/handover`, {}));
    if (!res) return;
    t.setData(res);
    if (res.picked_up) { toast(`${res.ticket} picked up.`, 'good'); return; }
    toast(`${res.ticket} closes as picked up once it’s paid.`, 'info');
    await checkout(res);
  };
  const notNow = async () => {
    const res = await run(() => post(`/api/repairs/${id}/handover`, { on: false }));
    if (res) t.setData(res);
  };
  const addPhoto = async (stage: 'before' | 'after') => {
    const file = await pickPhoto({ camera: true });
    if (!file) return;
    try {
      const dataUrl = await photoToDataUrl(file);
      const res = await run(() => post(`/api/repairs/${id}/photos`, { stage, dataUrl }), { success: 'Photo saved.' });
      if (res) t.setData(res);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That photo could not be used.', 'error');
    }
  };
  const showPasscode = async () => {
    const res = await run(() => post(`/api/repairs/${id}/passcode`));
    if (res) setPasscode(res.passcode);
  };
  const addNote = async () => {
    const res = await run(() => post(`/api/repairs/${id}/notes`, { note }));
    if (res) { t.setData(res); setNote(''); }
  };
  const text = async () => {
    const res = await run(() => post(`/api/repairs/${id}/text`, {}));
    if (res) { toast(res.message, res.status === 'not_configured' ? 'info' : 'good'); t.reload(); }
  };
  // Deposits paid ahead (not refunded) come off what's due at pickup.
  const deposits: { open_cents: number; max_cents: number; list: any[] } = r.deposits ?? { open_cents: 0, max_cents: 0, list: [] };
  const due = r.paid ? 0 : Math.max(0, r.totals.total_cents - deposits.open_cents);

  return (
    <div className="page">
      <div className="page-head">
        <div className="row" style={{ gap: 16 }}>
          {r.job ? <span className="thumb job-thumb" style={{ width: 72, height: 72 }} aria-hidden="true"><Icon.wrench /></span> : <span className="thumb" style={{ width: 72, height: 72 }}><ModelPhoto brand={r.brand} model={r.model} color={r.color} size={72} /></span>}
          <div>
            <div className="small strong muted">{r.job?.kind === 'vehicle' ? `Work order ${r.ticket}` : r.ticket}</div>
            <h1 style={{ margin: 0 }}>{r.job ? r.device_name : `${r.brand} ${r.model}`}</h1>
            <div className="row-wrap" style={{ gap: 8, marginTop: 4 }}>
              <StatusPill status={r.status} />
              {r.paid ? (r.paid.refunded ? <Badge tone="amber">Refunded · receipt #{r.paid.receipt_no}</Badge> : <Badge tone="green" icon="check">Paid · receipt #{r.paid.receipt_no}</Badge>) : null}
              {r.repair_time ? <Badge tone="blue" icon="clock">{r.repair_time}</Badge> : null}
              {r.warranty_of ? <Badge tone="amber">Warranty for {r.warranty_of.ticket}</Badge> : null}
              {r.external_ticket ? <Badge tone="blue">{r.external_ticket === r.ticket ? 'From RepairDesk' : `From RepairDesk · ${r.external_ticket}`}</Badge> : null}
            </div>
          </div>
        </div>
        <div className="row-wrap">
          {work && !r.job ? <Button icon="edit" onClick={() => setEditing(true)}>Edit ticket</Button> : null}
          {r.status === 'picked_up' && can('repair.create') ? (
            <Button icon="refresh" onClick={() => navigate(`/repairs/new?warranty=${r.id}`)} title="Check it in again under warranty: same customer and device">Warranty re-check</Button>
          ) : null}
          <Button icon="printer" busy={receiptJob?.state === 'sending'} onClick={() => printTicket('pickup')} title={onPrinter('receipt') ? `Prints on ${onPrinter('receipt')}` : 'Receipt printer (80 mm thermal)'}>Print Thermal Receipt</Button>
          <Button icon="tag" busy={techSending} onClick={() => printTicket('tech')} title={techPrinters ? `Prints on ${techPrinters}` : techPaper}>Print Technician Label</Button>
          <Button icon="printer" variant={r.job ? 'secondary' : 'ghost'} onClick={() => printTicket('letter')} title="Regular paper">{r.job ? (r.job.kind === 'vehicle' ? (r.paid ? 'Print invoice' : 'Print work order') : 'Print full page') : 'Full page'}</Button>
        </div>
      </div>

      {r.open && can('repair.work') ? (
        <div className="card stack-sm">
          {r.pickup_on_pay ? (
            <div className="banner good" role="status">
              <Icon.user />
              <div className="grow"><div className="strong">The customer is here.</div><div className="small">It closes as picked up once it’s paid.</div></div>
              <Button size="sm" variant="ghost" busy={busy} onClick={notNow}>Not taking it yet</Button>
            </div>
          ) : null}
          <div className="row-wrap" style={{ gap: 10 }}>
            {r.status === 'ready' ? (
              r.paid || r.totals.subtotal_cents === 0
                ? <Button variant="primary" size="lg" icon="check" busy={busy} onClick={() => setStatus('picked_up')}>Picked up</Button>
                : <Button variant="primary" size="lg" icon="bag" onClick={() => checkout()}>Check out {formatCents(due)}</Button>
            ) : null}
            {(NEXT_STEPS[r.status as RepairStatus] ?? []).map((s) => {
              // A work order or job ticket waiting for the customer's OK: "they said yes" records it (how, who), then the work resumes.
              const recordOk = !!r.job && r.status === 'waiting_customer' && s.status === 'in_progress' && r.job.needs_approval && r.lines.length > 0;
              return (
                <Button key={s.status} variant={s.primary && r.status !== 'ready' ? 'primary' : 'secondary'} size="lg" busy={busy} onClick={() => (recordOk ? setApproving(true) : setStatus(s.status))}>
                  {recordOk ? 'Customer said yes: record it' : r.job?.kind === 'vehicle' ? s.label.replace('Start repair', 'Start work').replace('Back to repair', 'Back to work') : s.label}
                </Button>
              );
            })}
            {/* A ready one already closes when it's paid (Check out / Picked up above). */}
            {r.status !== 'ready' ? (
              <Button size="lg" icon="user" busy={busy} onClick={() => (owes ? customerHere() : setHandOver(true))} title={owes ? 'Takes payment now; the ticket closes as picked up once it’s paid' : 'Nothing to pay: closes it as picked up'}>
                Customer is here
              </Button>
            ) : null}
            {r.status !== 'ready' && !r.paid && r.lines.length ? <Button size="lg" icon="bag" onClick={() => checkout()}>Take payment now</Button> : null}
            {r.status !== 'ready' && !r.paid && r.lines.length && deposits.max_cents > 0 && can('sale.create') ? <Button size="lg" icon="cash" onClick={() => setDepositing(true)}>Take deposit</Button> : null}
            <span className="grow" />
            {r.customer.phone && own ? <Button icon="chat" onClick={text}>Text customer</Button> : null}
            <Button variant="danger" icon={can('repair.cancel') ? 'close' : 'lock'} onClick={() => setCancel(true)} title={can('repair.cancel') ? undefined : 'Needs the owner’s PIN'}>{r.job?.kind === 'vehicle' ? 'Cancel work order' : 'Cancel repair'}</Button>
          </div>
        </div>
      ) : null}

      <div className="grid-2" style={{ alignItems: 'start' }}>
        <section className="stack">
          {r.job ? (
            <>
              <JobLinesCard r={r} work={work} onChanged={t.setData}>
                {r.totals.cost_cents !== null && r.totals.cost_cents !== undefined ? <><dt>Parts cost and profit</dt><dd><span className="pill-row"><Cost cents={r.totals.cost_cents} /><Profit cents={r.totals.profit_cents} /></span></dd></> : null}
                {deposits.list.map((d) => (
                  <Fragment key={d.line_id}>
                    <dt>Deposit</dt>
                    <dd><Money cents={d.amount_cents} /> · <a href={`#/sales/${d.sale_id}`}>receipt #{d.receipt_no}</a>{d.refunded ? ' · refunded' : d.used_on ? ` · used on receipt #${d.used_on.receipt_no}` : ''}</dd>
                  </Fragment>
                ))}
                {!r.paid && deposits.open_cents > 0 ? <><dt className="strong">Left to pay</dt><dd className="strong"><Money cents={due} /></dd></> : null}
                {r.paid ? <><dt>Paid</dt><dd><a href={`#/sales/${r.paid.sale_id}`}>Receipt #{r.paid.receipt_no}</a></dd></> : null}
              </JobLinesCard>
              <ApprovalsCard r={r} work={can('repair.work') && r.open} onChanged={t.setData} />
              <DiagnosisCard r={r} work={can('repair.work') && r.open} onChanged={t.setData} />
            </>
          ) : (
          <div className="card stack-sm">
            <div className="spread"><h3>Repairs</h3>{work && !r.paid ? <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add a repair</Button> : null}</div>
            {work && !r.paid && r.lines.length ? <p className="small muted">Tap a repair to change its price or name, pick the part you used, or remove it. The part never changes the price; only its sales tax is added.</p> : null}
            {r.paid ? <p className="small muted">Paid, so the prices are locked. To change them, refund receipt #{r.paid.receipt_no} first.{fixParts && r.lines.length ? ' You can still tap a repair to put the part on it or fix its taxed parts value (for the sales tax report); what was paid stays the same.' : ''}</p> : null}
            {!r.paid && !r.open && fixParts && r.lines.length ? <p className="small muted">Closed, but you can still tap a repair to put the part on it or fix its taxed parts value (for the sales tax report).</p> : null}
            {r.lines.length ? (
              <div className="list">
                {r.lines.map((l: any) => (
                  <button key={l.id} type="button" className="list-row" onClick={() => ((work && !r.paid) || fixParts ? setEditLine(l) : undefined)} style={{ cursor: (work && !r.paid) || fixParts ? 'pointer' : 'default' }}>
                    <span className="main-col">
                      <span className="t" style={{ display: 'block' }}>{l.description}</span>
                      <span className="s money-split" style={{ display: 'block' }}>
                        {l.parts_cents ? `Taxed parts ${formatCents(l.parts_cents)} · labor ${formatCents(l.labor_cents)}` : 'Labor only (no tax)'}
                      </span>
                      {l.product ? <span className="s part-used" style={{ display: 'block' }}><Icon.check /> Part used: {l.product.name}{l.part_cost_cents !== null ? <> <Cost cents={l.part_cost_cents} /></> : null}</span>
                        : l.supplier_part ? <span className="s part-used" style={{ display: 'block' }}><Icon.check /> Part used: {l.supplier_part.name ?? 'part'} ({l.supplier_part.supplier_name}){l.part_cost_cents ? <> <Cost cents={l.part_cost_cents} /></> : null}</span>
                        : work && !r.paid && l.parts_cents ? <span className="s" style={{ display: 'block', color: 'var(--blue)' }}>Tap to pick the part you used</span> : null}
                      {l.warranty ? <span className="s" style={{ display: 'block' }}>{l.warranty}</span> : null}
                    </span>
                    <span className="row" style={{ gap: 6 }}>
                      <Money cents={l.price_cents} className="strong" />
                      {work && !r.paid ? <span className="line-edit" aria-hidden="true"><Icon.edit /></span> : null}
                    </span>
                  </button>
                ))}
              </div>
            ) : <p className="muted">No repairs priced yet.{work ? ' Tap “Add a repair”.' : ''}</p>}
            <dl className="kv">
              <dt>Repair price</dt><dd><Money cents={r.totals.subtotal_cents} /></dd>
              {r.totals.cost_cents !== null && r.totals.cost_cents !== undefined ? <><dt>Parts cost and profit</dt><dd><span className="pill-row"><Cost cents={r.totals.cost_cents} /><Profit cents={r.totals.profit_cents} /></span></dd></> : null}
              <dt>{r.totals.tax_removed ? 'Tax removed at checkout' : <>Sales tax {formatRatePpm(r.totals.tax_rate_ppm)} on {formatCents(r.totals.parts_cents)} of parts</>}</dt><dd><Money cents={r.totals.tax_cents} /></dd>
              <dt className="strong">Total</dt><dd className="strong"><Money cents={r.totals.total_cents} /></dd>
              {deposits.list.map((d) => (
                <Fragment key={d.line_id}>
                  <dt>Deposit</dt>
                  <dd><Money cents={d.amount_cents} /> · <a href={`#/sales/${d.sale_id}`}>receipt #{d.receipt_no}</a>{d.refunded ? ' · refunded' : d.used_on ? ` · used on receipt #${d.used_on.receipt_no}` : ''}</dd>
                </Fragment>
              ))}
              {!r.paid && deposits.open_cents > 0 ? <><dt className="strong">Left to pay</dt><dd className="strong"><Money cents={due} /></dd></> : null}
              {r.paid ? <><dt>Paid</dt><dd><a href={`#/sales/${r.paid.sale_id}`}>Receipt #{r.paid.receipt_no}</a></dd></> : null}
            </dl>
          </div>
          )}

          <div className="card stack-sm">
            <div className="spread"><h3>Photos</h3>{can('repair.work') ? <span className="row" style={{ gap: 6 }}><Button size="sm" icon="plus" onClick={() => addPhoto('before')}>Before</Button><Button size="sm" icon="plus" onClick={() => addPhoto('after')}>After</Button></span> : null}</div>
            {r.photos.length ? (
              <div className="photos">
                {r.photos.map((p: any) => <a key={p.id} href={p.url} target="_blank" rel="noreferrer" title={`${p.stage} · ${formatDateTime(p.created_at)}`}><img src={p.url} alt={`${p.stage} photo`} loading="lazy" /></a>)}
              </div>
            ) : <p className="small muted">Take before photos of any damage, and after photos when it's fixed.</p>}
          </div>

          <div className="card stack-sm">
            <h3>Notes and history</h3>
            {can('repair.work') ? (
              <div className="row" style={{ gap: 8 }}>
                <input className="input grow" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for the team" onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) addNote(); }} />
                <Button onClick={addNote} disabled={!note.trim()} busy={busy}>Add</Button>
              </div>
            ) : null}
            <div className="timeline">
              {r.events.map((e: any) => (
                <div key={e.id} className={`ev ${e.kind === 'status' ? 'status' : ''}`}>
                  <div>
                    <div className="small"><span className="strong">{e.kind === 'status' && e.status ? REPAIR_STATUS_LABELS[e.status as RepairStatus] : e.note}</span>{e.kind === 'status' && e.note ? ` · ${e.note}` : ''}</div>
                    <div className="tiny muted">{formatDateTime(e.created_at)}{e.employee_name ? ` · ${e.employee_name}` : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="stack">
          <a className="card card-tight row" href={`#/customers/${r.customer.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            <span className="avatar avatar-sm">{r.customer.name.slice(0, 1)}</span>
            <span className="grow"><span className="strong" style={{ display: 'block' }}>{r.customer.name}</span><span className="small muted">{formatPhone(r.customer.phone) || 'No phone'}</span></span>
            <Icon.chevronRight />
          </a>
          {r.job ? <JobItemCard r={r} work={work} onChanged={t.setData} /> : null}
          {r.job ? <JobIntakeCard r={r} /> : null}
          {r.job ? null : <div className="card stack-sm">
            <div className="spread"><h3>Device</h3>{work ? <Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(true)}>Edit</Button> : null}</div>
            <dl className="kv">
              <dt>Problem</dt><dd>{r.issue}</dd>
              {r.color ? <><dt>Color</dt><dd>{r.color}</dd></> : null}
              {r.imei ? <><dt>IMEI</dt><dd className="num row-wrap">{r.imei}<PhoneCheckButton code={r.imei} size="sm" /></dd></> : null}
              {r.serial ? <><dt>Serial</dt><dd className="num row-wrap">{r.serial}{r.imei ? null : <PhoneCheckButton code={r.serial} size="sm" />}</dd></> : null}
              <dt>Passcode</dt>
              <dd>
                {r.has_passcode ? (can('repair.passcode') ? (passcode ? <span className="strong num" style={{ fontSize: 20 }}>{passcode}</span> : <Button size="sm" icon="lock" onClick={showPasscode}>Show passcode</Button>) : 'On file') : r.passcode_consent ? 'Not given' : 'Customer didn’t share it'}
              </dd>
              {r.condition_notes ? <><dt>Condition</dt><dd>{r.condition_notes}</dd></> : null}
              {r.left_with.length ? <><dt>Left with it</dt><dd>{r.left_with.join(', ')}</dd></> : null}
              <dt>Checked in</dt><dd>{formatDateTime(r.created_at)}{r.created_by_name ? ` by ${r.created_by_name}` : ''}</dd>
              {r.repair_time ? <><dt>Repair time</dt><dd>{r.repair_time}</dd></> : null}
              {r.promised_at ? <><dt>Ready by</dt><dd>{when(r.promised_at)}</dd></> : null}
              <dt>Technician</dt><dd>{r.technician?.name ?? 'Anyone'}</dd>
              <dt>Store</dt><dd>{r.location_name}</dd>
            </dl>
          </div>}
          {!r.job && Object.keys(r.precheck).length ? (
            <div className="card stack-sm">
              <h3>Drop-off check</h3>
              <dl className="kv">
                {PRECHECK_ITEMS.filter((it) => r.precheck[it.key]).map((it) => (
                  <Fragment key={it.key}><dt>{it.label}</dt><dd className={r.precheck[it.key] === 'bad' ? 'overdue' : ''}>{PRECHECK_LABELS[r.precheck[it.key] as PrecheckValue]}</dd></Fragment>
                ))}
              </dl>
            </div>
          ) : null}
        </section>
      </div>

      {printNow ? (
        <Sheet title={r.job?.kind === 'vehicle' ? `Work order ${r.ticket} saved` : `${r.ticket} checked in`} onClose={() => setPrintNow(false)} footer={<Button variant="primary" onClick={() => setPrintNow(false)}>Done</Button>}>
          <p className="muted" style={{ marginTop: 0 }}>Print both in one tap, or one at a time. You can print them again any time from this ticket.</p>
          <div className="print-now">
            {r.job ? (
              <button type="button" className="pn-btn" style={{ gridColumn: '1 / -1', minHeight: 0 }} onClick={() => printTicket('letter')}>
                <Icon.printer /><span className="t">{r.job.kind === 'vehicle' ? 'Print the work order' : 'Print the full-page ticket'}</span>
                <span className="s">Regular paper: the {r.job.kind === 'vehicle' ? 'vehicle' : 'item'}, parts and labor, and the customer’s approval</span>
              </button>
            ) : null}
            <button type="button" className="pn-btn" style={{ gridColumn: '1 / -1', minHeight: 0 }} disabled={techSending || receiptJob?.state === 'sending'} onClick={printBoth}>
              <Icon.printer /><span className="t">Print both</span>
              <span className="s">The customer’s receipt, then the technician’s label</span>
            </button>
            <button type="button" className={`pn-btn ${techDone || (!techJobs.length && printedNow.includes('tech')) ? 'done' : ''}`} disabled={techSending} onClick={() => printOne('tech')}>
              <Icon.tag /><span className="t">Print Technician Label</span>
              <span className="s">{techPaper}{techPrinters ? ` · ${techPrinters}` : ''}</span>
              {techJobs.length ? techJobs.map((j) => <PrintState key={j.key} job={j} tapped />) : <PrintState job={undefined} tapped={printedNow.includes('tech')} />}
            </button>
            <button type="button" className={`pn-btn ${receiptJob?.state === 'printed' || (!receiptJob && printedNow.includes('pickup')) ? 'done' : ''}`} disabled={receiptJob?.state === 'sending'} onClick={() => printOne('pickup')}>
              <Icon.printer /><span className="t">Print Thermal Receipt</span>
              <span className="s">Customer copy, 80 mm receipt{onPrinter('receipt') ? ` · ${onPrinter('receipt')}` : ''}</span>
              <PrintState job={receiptJob} tapped={printedNow.includes('pickup')} />
            </button>
          </div>
        </Sheet>
      ) : null}
      {cancel ? (
        <CancelRepairsSheet
          tickets={[{ id: r.id, ticket: r.ticket, paid: !!r.paid, name: `${r.brand} ${r.model} · ${r.customer.name}` }]}
          onClose={() => setCancel(false)}
          onDone={() => { setCancel(false); t.reload(); }}
        />
      ) : null}
      {handOver ? (
        <Confirm title={`Hand over ${r.ticket}?`} confirmLabel="Picked up" busy={busy} onClose={() => setHandOver(false)} onConfirm={customerHere}>
          <p style={{ marginTop: 0 }}>
            {r.paid ? `It’s paid (receipt #${r.paid.receipt_no})` : 'There’s nothing to pay'}, so it closes as <strong>Picked up</strong> now.
            {' '}It still says “{REPAIR_STATUS_LABELS[r.status as RepairStatus]}”.
          </p>
        </Confirm>
      ) : null}
      {depositing ? <DepositSheet repair={r} onClose={() => setDepositing(false)} /> : null}
      {approving ? (
        <ApprovalSheet
          r={r}
          onClose={() => setApproving(false)}
          onDone={(res) => {
            setApproving(false);
            t.setData(res);
            // Approved (it's back to checked in): the work resumes, as "Customer said yes, resume" always did.
            if (res?.status === 'checked_in') void setStatus('in_progress');
          }}
        />
      ) : null}
      {adding ? <AddLineSheet repair={r} onClose={() => setAdding(false)} onDone={(res) => { t.setData(res); setAdding(false); }} /> : null}
      {editLine ? <LineSheet repair={r} line={editLine} partsOnly={partsOnly} onClose={() => setEditLine(null)} onDone={(res) => { t.setData(res); setEditLine(null); }} /> : null}
      {editing ? <EditRepairSheet repair={r} onClose={() => setEditing(false)} onDone={(res) => { t.setData(res); setEditing(false); }} /> : null}
    </div>
  );
}

/** How a print from the "checked in" window is going (PrintNode), or that it went to the print window. */
function PrintState({ job, tapped }: { job: PrintJob | undefined; tapped: boolean }) {
  if (!job) return tapped ? <span className="pn-state printed">Sent to the print window</span> : null;
  const warn = job.stuck || job.slow;
  return (
    <span className={`pn-state ${job.state} ${warn ? 'warn' : ''}`} role="status">
      {job.state === 'failed' ? `Didn’t print: ${jobText(job)} Tap to try again.` : jobText(job)}
    </span>
  );
}

type RefundMethod = 'cash' | 'card' | 'store_credit';
const refundChoices = (own: boolean): [RefundMethod, string][] => [['cash', 'Cash from drawer'], ['card', own ? 'Card (refund on the Clover)' : 'Card (refund on your card reader)'], ['store_credit', 'Store credit']];
const refundWords = (own: boolean): Record<RefundMethod, string> => ({ cash: 'in cash from the drawer', card: own ? 'to the card (run it back on the Clover)' : 'to the card (run it back on your card reader)', store_credit: 'in store credit' });

interface CancelResult {
  cancelled: string[];
  failed: { id: string; ticket: string | null; message: string }[];
  refunds: { ticket: string; refund_no: number; total_cents: number; method: RefundMethod }[];
  refunded_cents: number;
}

/**
 * Cancels one ticket or the ones picked in the list (returned to the customer unrepaired): one
 * reason for all of them. Only the owner can: anyone else is asked for the owner's PIN. A paid one
 * gives back its full amount, and the owner picks how for each: cash, card or store credit.
 */
function CancelRepairsSheet({ tickets, onClose, onDone }: { tickets: { id: string; ticket: string; paid: boolean; name: string }[]; onClose: () => void; onDone: () => void }) {
  const { can } = useSession();
  const own = usePlan().home;
  const toast = useToast();
  const { run, busy } = useAction();
  const [reason, setReason] = useState('');
  const [methods, setMethods] = useState<Record<string, RefundMethod>>({});
  const ids = tickets.map((x) => x.id);
  // What each one gives back (the server works it out): a paid one its own lines on the receipt
  // that paid it, with their tax, and any deposit paid on it.
  const paid = useLoad<{ tickets: { id: string; ticket: string; customer_name: string | null; paid: boolean; refund_cents: number; receipts: number[] }[] }>(
    () => post('/api/repairs/cancel/preview', { ids }), [ids.join(',')],
  );
  const refunds = (paid.data?.tickets ?? []).filter((x) => x.refund_cents > 0);
  const owed = refunds.reduce((s, x) => s + x.refund_cents, 0);
  const loading = !paid.data && !paid.error;
  const ready = !!reason.trim() && !loading && !paid.error && refunds.every((x) => methods[x.id]);
  const one = tickets.length === 1;
  const names = tickets.slice(0, 6).map((x) => x.ticket).join(', ') + (tickets.length > 6 ? ` and ${tickets.length - 6} more` : '');
  const submit = async () => {
    const res = await run((approval) => post<CancelResult>('/api/repairs/cancel', {
      ids: tickets.map((x) => x.id), note: reason.trim(),
      refunds: Object.fromEntries(refunds.map((x) => [x.id, methods[x.id]])), approval,
    }));
    if (!res) return;
    const why = res.failed.slice(0, 3).map((f) => `${f.ticket ?? 'a ticket'}: ${f.message}`).join('; ') + (res.failed.length > 3 ? '…' : '');
    // Nothing went through: say why and stay open (pick another way to refund, open the drawer…).
    if (!res.cancelled.length) { toast(why || 'Nothing was cancelled.', 'error'); return; }
    const by = (m: RefundMethod) => res.refunds.filter((x) => x.method === m).reduce((s, x) => s + x.total_cents, 0);
    const back = (['cash', 'card', 'store_credit'] as RefundMethod[]).filter((m) => by(m) > 0).map((m) => `${formatCents(by(m))} ${refundWords(own)[m]}`).join(', ');
    const n = res.cancelled.length;
    toast(
      `${n === 1 ? res.cancelled[0] : `${n} repairs`} cancelled.${back ? ` Refunded ${back}.` : ''}${res.failed.length ? ` ${res.failed.length} couldn’t be: ${why}` : ''}`,
      res.failed.length ? 'info' : 'good',
    );
    onDone();
  };
  return (
    <Sheet
      title={one ? `Cancel ${tickets[0].ticket}?` : `Cancel ${tickets.length} repairs?`}
      onClose={onClose}
      wide={refunds.length > 0}
      footer={<><Button onClick={onClose}>Keep repairing</Button><Button variant="danger" busy={busy} disabled={!ready} onClick={submit}>{one ? 'Cancel repair' : `Cancel ${tickets.length} repairs`}{owed ? ` · refund ${formatCents(owed)}` : ''}</Button></>}
    >
      {!can('repair.cancel') ? (
        <div className="banner"><Icon.lock /><div><div className="strong">Only the owner can cancel repairs.</div><div className="small">You’ll be asked for the owner’s PIN.</div></div></div>
      ) : null}
      <p style={{ marginTop: 0 }}>
        {one ? <><strong>{tickets[0].name}</strong> closes</> : <><strong>{names}</strong> close</>} as returned, not repaired. Any parts on {one ? 'it' : 'them'} go back into stock.
      </p>
      {loading ? <Spinner /> : null}
      {paid.error ? <div className="error-text" role="alert">The paid tickets couldn’t be loaded. Close this and try again.</div> : null}
      {refunds.length ? (
        <div className="stack-sm">
          <div className="strong">{refunds.length === 1 ? (refunds[0].paid ? 'It was paid: the full amount goes back.' : 'A deposit was paid on it: all of it goes back.') : `${refunds.length} were paid: each gets its full amount back.`} How?</div>
          {refunds.length > 1 ? (
            <div className="row-wrap" style={{ gap: 6 }}>
              <span className="small muted">All of them:</span>
              {refundChoices(own).map(([v, label]) => (
                <button key={v} type="button" className="chip" aria-pressed={refunds.every((x) => methods[x.id] === v)} onClick={() => setMethods(Object.fromEntries(refunds.map((x) => [x.id, v])))}>{label}</button>
              ))}
            </div>
          ) : null}
          {refunds.map((x) => (
            <div key={x.id} className="card card-tight stack-sm">
              <div className="spread">
                <span><span className="strong">{x.ticket}</span> <span className="small muted">· {x.customer_name}{x.receipts.length ? ` · receipt #${x.receipts.join(', #')}` : ''}</span></span>
                <Money cents={x.refund_cents} className="strong" />
              </div>
              <div className="chips" role="group" aria-label={`Refund ${x.ticket} as`}>
                {refundChoices(own).map(([v, label]) => (
                  <button key={v} type="button" className="chip" aria-pressed={methods[x.id] === v} onClick={() => setMethods((m) => ({ ...m, [x.id]: v }))}>{label}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="chips">
        {['Customer declined the price', 'Can’t be fixed', 'Part not available', 'Customer picked it up early'].map((x) => <button key={x} type="button" className="chip" aria-pressed={reason === x} onClick={() => setReason(x)}>{x}</button>)}
      </div>
      <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} /></Field>
    </Sheet>
  );
}

function AddLineSheet({ repair, onDone, onClose }: { repair: any; onDone: (r: any) => void; onClose: () => void }) {
  const { run, busy } = useAction();
  const options = useRepairOptions(repair.brand, repair.model);
  const [d, setD] = useState('');
  const [p, setP] = useState<number | null>(null);
  const add = async (body: any) => {
    const res = await run(() => post(`/api/repairs/${repair.id}/lines`, body), { success: 'Added. Tap it to pick the part you used.' });
    if (res) onDone(res);
  };
  return (
    <Sheet title="Add a repair" onClose={onClose} wide>
      {options.loading && !options.data ? <Spinner label="Getting prices" /> : null}
      {options.data?.repairs.length ? (
        <div className="svc-grid">
          {options.data.repairs.flatMap((r) => r.options.filter((o) => o.price_cents !== null).map((o) => (
            <button key={o.id} type="button" className="svc svc-pic" disabled={busy} onClick={() => add({ description: o.label, priceCents: o.price_cents ?? 0, hubOptionId: o.id, warranty: o.warranty })}>
              <Photo src={repairPhotoFor(o.label, repair.device_type)} alt="" size={40} className="svc-pic-img" />
              <span className="t">{o.label}</span>
              <span className="s">{[o.time, o.warranty].filter(Boolean).join(' · ')}</span>
              <span className="p">{o.from ? 'From ' : ''}{formatCents(o.price_cents ?? 0)}</span>
            </button>
          )))}
        </div>
      ) : null}
      <div className="card stack-sm">
        <h3>Something else</h3>
        <div className="form-grid">
          <Field label="Repair"><input className="input" value={d} onChange={(e) => setD(e.target.value)} /></Field>
          <Field label="Price" hint="the part is included in it"><MoneyInput value={p} onChange={setP} /></Field>
        </div>
        <div><Button variant="primary" busy={busy} disabled={!d.trim() || p === null} onClick={() => add({ description: d.trim(), priceCents: p ?? 0 })}>Add</Button></div>
      </div>
    </Sheet>
  );
}

/**
 * One repair on a ticket: its name and price, the taxed parts value, and the part used (from a
 * supplier or from stock). `partsOnly` is a paid or closed ticket: the name and price are locked
 * and only the part and its taxed value can change (a manager's fix for the sales tax report).
 */
function LineSheet({ repair, line, partsOnly = false, onDone, onClose }: { repair: any; line: any; partsOnly?: boolean; onDone: (r: any) => void; onClose: () => void }) {
  const { run, busy } = useAction();
  const [name, setName] = useState<string>(line.description);
  const [price, setPrice] = useState<number | null>(line.price_cents);
  const [parts, setParts] = useState<number | null>(line.parts_cents);
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const found = useLoad<any[]>(() => get(`/api/repair-parts${qs({ model: repair.model, q: dq })}`), [dq]);
  const save = async (body: any) => {
    // A paid or closed ticket takes only the part and its taxed value, through its own route.
    const partsBody = { partsCents: body.partsCents, productId: body.productId, supplier: body.supplier, supplierSku: body.supplierSku, supplierUrl: body.supplierUrl, partName: body.partName };
    const res = await run(() => patch(partsOnly ? `/api/repairs/${repair.id}/lines/${line.id}/parts` : `/api/repairs/${repair.id}/lines/${line.id}`, partsOnly ? partsBody : body), { success: 'Saved.' });
    if (res) onDone(res);
  };
  const remove = async () => {
    const res = await run(() => del(`/api/repairs/${repair.id}/lines/${line.id}`), { success: 'Removed.' });
    if (res) onDone(res);
  };
  const { can } = useSession();
  const own = usePlan().home;
  // A supplier's part: it goes on this repair without changing the price; its value is the taxed parts amount.
  const useSupplierPart = (x: RepairPart) => {
    const sp = line.supplier_part;
    if (sp && repairPartKey({ supplier: sp.supplier, sku: sp.sku, url: sp.url, name: sp.name ?? '' }) === repairPartKey(x)) return save({ supplier: null });
    return save({ supplier: x.supplier, supplierSku: x.sku, supplierUrl: x.url, partName: x.name, partsCents: x.price_cents ?? null });
  };
  const g = repairLineGroup(line.description);
  return (
    <Sheet title={line.description} onClose={onClose} wide footer={<>{partsOnly ? null : <Button variant="danger" busy={busy} onClick={remove}>Remove</Button>}<span className="grow" /><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!name.trim()} onClick={() => save({ description: name.trim(), priceCents: price ?? 0, partsCents: parts })}>Save</Button></>}>
      {partsOnly ? <div className="banner"><Icon.tag /><div className="small">{repair.paid ? 'Paid: the price is locked, but the part and its taxed value can still be fixed for the sales tax report. The receipt’s parts and labor are split again; what the customer paid doesn’t change.' : 'Closed: the price is locked, but the part and its taxed value can still be fixed for the sales tax report.'}</div></div> : null}
      <div className="form-grid">
        <Field label="Repair" className="full"><input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} disabled={partsOnly} /></Field>
        <Field label="Price" hint="the part is included in it"><MoneyInput value={price} onChange={setPrice} autoFocus={!partsOnly} disabled={partsOnly} /></Field>
        <Field label="Parts value (taxed)" hint="the rest is labor, which isn’t taxed"><MoneyInput value={parts} onChange={setParts} autoFocus={partsOnly} /></Field>
      </div>
      {own ? (
        <div className="stack-sm">
          <div className="spread"><h3>Part from a supplier</h3>{line.supplier_part ? <Button size="sm" variant="ghost" busy={busy} onClick={() => save({ supplier: null })}>Take it off</Button> : null}</div>
          {line.supplier_part ? <div className="banner"><Icon.check /><div>Using <strong>{line.supplier_part.name ?? 'a part'}</strong> from {line.supplier_part.supplier_name}. The price stays the same; its value ({formatCents(line.parts_cents)}) is what’s taxed.</div></div> : null}
          <RepairPartsSearch
            model={partsQueryModel(repair.model)}
            startWord={g === 'battery' ? 'battery' : g === 'back' ? 'back glass' : g === 'charging' ? 'charging port' : g === 'camera' ? 'camera' : g === 'audio' ? 'speaker' : 'screen'}
            chosen={line.supplier_part ? [repairPartKey({ supplier: line.supplier_part.supplier, sku: line.supplier_part.sku, url: line.supplier_part.url, name: line.supplier_part.name ?? '' })] : []}
            onPick={useSupplierPart}
            showCost={can('inventory.view_cost')}
            busy={busy}
          />
        </div>
      ) : null}
      <div className="stack-sm">
        <div className="spread"><h3>{own ? 'Or a part from stock' : 'Part from stock'}</h3>{line.product ? <Button size="sm" variant="ghost" busy={busy} onClick={() => save({ productId: null })}>Put part back</Button> : null}</div>
        {line.product ? <div className="banner"><Icon.check /><div>Using <strong>{line.product.name}</strong>. It was taken out of stock.</div></div> : <p className="small muted">Pick the part you're using. It comes out of this store's stock and its cost shows on the profit report.</p>}
        <div className="search"><Icon.search /><input className="input" placeholder={`Parts for ${repair.model}, or search all parts`} value={q} onChange={(e) => setQ(e.target.value)} /></div>
        {found.data?.length ? (
          <div className="list">
            {found.data.map((p: any) => (
              <button key={p.id} type="button" className="list-row" disabled={busy || p.id === line.product?.id} onClick={() => save({ productId: p.id })}>
                <span className="thumb"><ProductPicture url={p.image_url} categoryId={p.category_id} name={p.name} size={56} /></span>
                <span className="main-col">
                  <span className="t" style={{ display: 'block' }}>{p.name}</span>
                  <span className="s" style={{ display: 'block' }}>{[p.sku, `${p.qty} here`].filter(Boolean).join(' · ')}{p.cost_cents !== null ? <> <Cost cents={p.cost_cents} /></> : null}</span>
                </span>
                {p.qty > 0 ? <Badge tone="green">In stock</Badge> : <Badge tone="red">Out</Badge>}
              </button>
            ))}
          </div>
        ) : found.data ? <p className="small muted">No parts found. Add parts in Inventory, or import your supplier's list.</p> : <Spinner />}
      </div>
    </Sheet>
  );
}

/**
 * Edit ticket: the device and problem, the repair time and ready-by time, the technician, the
 * drop-off check, what was left with it, the passcode (only with the customer's OK, like at
 * check-in; the one on file is never shown) and text updates. The ticket's history notes only
 * what was really changed here.
 */
function EditRepairSheet({ repair, onDone, onClose }: { repair: any; onDone: (r: any) => void; onClose: () => void }) {
  const { status } = useSession();
  const own = usePlan().home;
  const { run, busy } = useAction();
  const startReadyBy = repair.promised_at ? localInputValue(repair.promised_at) : '';
  const [f, setF] = useState({
    brand: repair.brand as string, model: repair.model as string,
    issue: repair.issue as string, color: (repair.color ?? '') as string, imei: (repair.imei ?? '') as string, serial: (repair.serial ?? '') as string,
    conditionNotes: (repair.condition_notes ?? '') as string, technicianId: (repair.technician?.id ?? '') as string,
    readyBy: startReadyBy,
  });
  const kind: DeviceType = isDeviceType(repair.device_type) ? repair.device_type : deviceTypeOf(repair.brand, repair.model);
  // This device's checks, and any other check already answered on the ticket.
  const checks = [...precheckItemsFor(kind), ...PRECHECK_ITEMS.filter((it) => !precheckItemsFor(kind).some((c) => c.key === it.key) && repair.precheck?.[it.key])];
  const [pre, setPre] = useState<Record<string, PrecheckValue>>(repair.precheck ?? {});
  const leftOptions = [...LEFT_WITH_BY_TYPE[kind], ...((repair.left_with ?? []) as string[]).filter((x) => !LEFT_WITH_BY_TYPE[kind].includes(x))];
  const [left, setLeft] = useState<string[]>(repair.left_with ?? []);
  const [minutes, setMinutes] = useState<number | null>(repair.repair_minutes ?? null);
  const [consent, setConsent] = useState<boolean>(!!repair.passcode_consent);
  const [passcode, setPasscode] = useState('');
  const [notify, setNotify] = useState<boolean>(!!repair.notify_sms);
  const phone = !!repair.customer?.phone;
  // Which of these were changed here: only those are sent.
  const [changed, setChanged] = useState<string[]>([]);
  const mark = (k: string) => setChanged((c) => (c.includes(k) ? c : [...c, k]));
  const pickTime = (m: number) => {
    setMinutes(m);
    mark('time');
    // A new repair time moves the ready-by time to match (it can still be changed).
    setF((x) => ({ ...x, readyBy: localInputValue(Date.now() + m * 60_000) }));
  };
  const techs = status?.device?.employees ?? [];
  const save = async () => {
    const { readyBy, ...rest } = f;
    const body: Record<string, unknown> = {
      ...rest, brand: f.brand.trim() || repair.brand, model: f.model.trim() || repair.model, issue: f.issue.trim() || repair.issue,
      imei: f.imei || null, serial: f.serial || null, color: f.color || null, conditionNotes: f.conditionNotes || null, technicianId: f.technicianId || null,
    };
    if (readyBy !== startReadyBy) body.promisedAt = readyBy ? new Date(readyBy).getTime() : null;
    if (changed.includes('time')) body.repairMinutes = minutes;
    if (changed.includes('precheck')) body.precheck = pre;
    if (changed.includes('left')) body.leftWith = left;
    if (changed.includes('notify')) body.notifySms = notify;
    // Unticking the customer's OK erases the passcode on file; a new one replaces it; an empty box keeps it.
    if (consent !== !!repair.passcode_consent) body.passcodeConsent = consent;
    if (consent && passcode.trim()) { body.passcodeConsent = true; body.passcode = passcode.trim(); }
    const res = await run(() => patch(`/api/repairs/${repair.id}`, body), { success: 'Saved.' });
    if (res) onDone(res);
  };
  return (
    <Sheet title={`Edit ${repair.ticket}`} onClose={onClose} wide footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={save}>Save</Button></>}>
      <div className="form-grid">
        <Field label="Brand"><input className="input" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} /></Field>
        <Field label="Model"><input className="input" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} /></Field>
        <Field label="Problem" className="full"><textarea className="input" rows={2} value={f.issue} onChange={(e) => setF({ ...f, issue: e.target.value })} /></Field>
        <div className="full stack-sm">
          <span className="small strong">Repair time</span>
          <div className="chips" role="radiogroup" aria-label="Repair time">
            {[...REPAIR_TIMES, ...LONGER_REPAIR_TIMES].map((m) => (
              <button key={m} type="button" role="radio" className="chip" aria-checked={minutes === m} aria-pressed={minutes === m} onClick={() => pickTime(m)}>{repairTimeLabel(m)}</button>
            ))}
          </div>
        </div>
        <Field label="Ready by" hint={changed.includes('time') ? 'moved to match the repair time' : 'empty = no rush'}><input className="input" type="datetime-local" value={f.readyBy} onChange={(e) => setF({ ...f, readyBy: e.target.value })} /></Field>
        {techs.length ? (
          <Field label="Technician">
            <select className="select" value={f.technicianId} onChange={(e) => setF({ ...f, technicianId: e.target.value })}>
              <option value="">Anyone</option>
              {techs.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
        ) : null}
        <Field label="Color"><input className="input" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></Field>
        <Field label="IMEI"><input className="input" inputMode="numeric" value={f.imei} onChange={(e) => setF({ ...f, imei: e.target.value })} /></Field>
        <Field label="Serial"><input className="input" value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} /></Field>
        <Field label="Condition at drop-off" className="full"><input className="input" value={f.conditionNotes} onChange={(e) => setF({ ...f, conditionNotes: e.target.value })} /></Field>
      </div>
      <div className="stack-sm">
        <div className="spread">
          <span className="small strong">Drop-off check</span>
          <Button size="sm" icon="check" onClick={() => { setPre(allWork(checks)); mark('precheck'); }}>All work</Button>
        </div>
        <PrecheckGrid checks={checks} value={pre} onChange={(v) => { setPre(v); mark('precheck'); }} />
      </div>
      <div className="stack-sm">
        <span className="small strong">Left with the device</span>
        <div className="chips">
          {leftOptions.map((o) => (
            <button key={o} type="button" className="chip" aria-pressed={left.includes(o)} onClick={() => { setLeft(left.includes(o) ? left.filter((x) => x !== o) : [...left, o]); mark('left'); }}>{o}</button>
          ))}
        </div>
      </div>
      <div className="stack-sm">
        <label className="check"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /> The customer agrees to share the passcode so we can test the device</label>
        {consent ? (
          <Field label={repair.has_passcode ? 'New passcode' : 'Passcode'} hint={repair.has_passcode ? 'one is on file; leave this empty to keep it' : 'only managers and technicians can see it; erased at pickup'}>
            <input className="input" value={passcode} onChange={(e) => setPasscode(e.target.value)} autoComplete="off" />
          </Field>
        ) : repair.has_passcode ? <p className="small muted" style={{ margin: 0 }}>Saving erases the passcode on file.</p> : null}
      </div>
      {own ? (
        <label className="consent-box">
          <input type="checkbox" checked={notify} disabled={!phone} onChange={(e) => { setNotify(e.target.checked); mark('notify'); }} aria-label="Customer agrees to texts about this repair" />
          <span className="small"><strong>Text the customer when it’s ready{phone ? '' : ' (no phone on file)'}</strong><br /><span className="muted">Only with the customer’s yes. Optional. Msg & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.</span></span>
        </label>
      ) : null}
    </Sheet>
  );
}

// ---------------------------------------------------------------- printouts

interface TicketPrint {
  business_name: string;
  logo_url: string;
  website: string;
  terms: string;
  store: StoreInfo;
  ticket: string;
  created_at: number;
  promised_at: number | null;
  employee: string | null;
  customer: { name: string; phone: string | null };
  device: { brand: string; model: string; color: string | null; imei: string | null; serial: string | null; type?: string | null };
  issue: string;
  condition_notes: string | null;
  precheck: Record<string, PrecheckValue>;
  left_with: string[];
  passcode_note: string;
  lines: { description: string; price_cents: number; parts_cents?: number; warranty: string | null; part: string | null }[];
  totals: { subtotal_cents: number; tax_cents: number; total_cents: number; parts_cents?: number; tax_removed?: boolean };
  /** The repair time picked at check-in ("25-Minute Repair"). */
  repair_time?: string | null;
  paid: { receipt_no: number; total_cents: number; refunded_cents?: number | null; refunded?: boolean } | null;
  /** 'now': paying at drop-off; otherwise at pickup. */
  pay_plan?: string | null;
  status?: string | null;
  /** A warranty repair (its $0 price is final). */
  warranty?: boolean;
  technician: string | null;
  notes: { at: number; by: string | null; note: string }[];
}

function precheckText(p: Record<string, PrecheckValue>): string {
  return PRECHECK_ITEMS.filter((it) => p[it.key]).map((it) => `${it.label}: ${PRECHECK_LABELS[p[it.key]]}`).join(' · ');
}

/**
 * The customer's repair ticket (thermal): what we're fixing, the price, when it's ready, and the
 * ticket number and barcode to bring back at pickup. Kept short so it always comes out on ONE
 * receipt paper (a receipt longer than the printer's page is split onto a second paper).
 */
export function RepairPickupPaper({ p }: { p: TicketPrint }) {
  const pay = repairPayment(p);
  return (
    <div className="receipt">
      <ReceiptHeader logo={p.logo_url} business={p.business_name} store={p.store} website={p.website} />
      <div className="c strong">REPAIR TICKET <span className="copy-tag inline">CUSTOMER COPY</span></div>
      <div className="ticket-no">{p.ticket}</div>
      <div className="bar"><Barcode value={p.ticket} /></div>
      <dl className="meta">
        <dt>Dropped off</dt><dd>{formatDateTime(p.created_at)}</dd>
        {p.repair_time ? <><dt>Repair time</dt><dd className="strong">{p.repair_time}</dd></> : null}
        {p.promised_at ? <><dt>Ready by</dt><dd className="strong">{formatDateTime(p.promised_at)}</dd></> : null}
        <dt>Customer</dt><dd>{p.customer.name}</dd>
        {p.customer.phone ? <><dt>Phone</dt><dd>{formatPhone(p.customer.phone)}</dd></> : null}
        <dt>Device</dt><dd>{shortDeviceName(p.device)}{p.device.color ? ` · ${p.device.color}` : ''}</dd>
        {p.device.imei ? <><dt>IMEI</dt><dd>{p.device.imei}</dd></> : p.device.serial ? <><dt>Serial</dt><dd>{p.device.serial}</dd></> : null}
      </dl>
      <hr />
      <div><b>Problem:</b> {p.issue}</div>
      {p.left_with.length ? <div className="dim">Left with it: {p.left_with.join(', ')}</div> : null}
      <DropOffCheck p={p} />
      <hr />
      <table className="rtable">
        <thead><tr><th>REPAIR</th><th className="r">PRICE</th></tr></thead>
        <tbody>
          {p.lines.map((l, i) => <tr key={i}><td>{l.description}{l.warranty ? <div className="dim">{l.warranty}</div> : null}</td><td className="r">{formatCents(l.price_cents)}</td></tr>)}
        </tbody>
      </table>
      <hr />
      <div className="rl"><span>Repair price</span><span>{formatCents(p.totals.subtotal_cents)}</span></div>
      <div className="rl"><span>{p.totals.tax_removed ? 'Tax removed' : 'Sales tax (parts)'}</span><span>{formatCents(p.totals.tax_cents)}</span></div>
      <div className="rl tot"><span>{p.paid ? 'TOTAL' : 'ESTIMATE'}</span><span>{formatCents(p.totals.total_cents)}</span></div>
      <hr />
      <PayStampView pay={pay} />
      {pay.state === 'unpaid' ? <div className="dim c">Final price confirmed after a quick check in store.</div> : null}
      <hr />
      <div className="c strong pickup-note">Bring this ticket to pick up your device.</div>
      <ReceiptPolicies title="REPAIR POLICIES" text={p.terms} />
      <SiteLine website={p.website} />
      <ReceiptMark />
    </div>
  );
}

/** A time as the value of a datetime-local field (the tablet's own time zone). */
function localInputValue(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** When it's due: just the time today, or the day and time ("Sat 3:30 PM"). */
function dueText(ms: number): string {
  const tz = zone.current;
  const sameDay = dayKey(ms, tz) === dayKey(Date.now(), tz);
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, ...(sameDay ? {} : { weekday: 'short' as const }), hour: 'numeric', minute: '2-digit' }).format(new Date(ms));
}

/** The marks the drop-off check prints with. */
const MARK: Record<PrecheckValue | 'none', string> = { ok: '✓', bad: '✗', na: '?', none: '–' };

/**
 * The drop-off check on the customer's receipt (and the technician's ticket): every check for the
 * device with a big mark in a box, the problems filled in black so they stand out.
 */
export function DropOffCheck({ p, compact }: { p: TicketPrint; compact?: boolean }) {
  const rows = dropOffRows(p.device, p.precheck);
  if (!rows.length) return null;
  const one = !compact && rows.some((r) => r.label.length > 20);
  const blank = rows.some((r) => !r.value);
  return (
    <div className={`dropoff${compact ? ' sm' : ''}`}>
      <div className="do-h">DROP-OFF CHECK</div>
      {compact ? null : <div className="do-sub">{DROP_OFF_NOTE}</div>}
      <div className={`do-grid${one ? ' one' : ''}`}>
        {rows.map((r) => (
          <div key={r.key} className={`do-item ${r.value ?? 'none'}`}>
            <span className="do-mark">{MARK[r.value ?? 'none']}</span>
            <span className="do-label">{r.label}</span>
          </div>
        ))}
      </div>
      <div className="do-legend">✓ works · ✗ not working · ? couldn’t test{blank ? ' · – not checked' : ''}</div>
    </div>
  );
}

/** The logo on the technician's ticket: the West Coast Wireless name (without the address part), or the store's own logo. */
function techLogo(logo: string): string {
  return /\/wcw-receipt-logo\.png$/.test(logo) ? '/assets/wcw-wordmark.png' : logo;
}
const shortTime = (t: string | null | undefined) => (t ? t.replace(/-Minute Repair$/, ' min').replace(/-Hour Repair$/, ' hr').replace('Next-Day Repair', 'Next day') : null);
const failedChecks = (p: TicketPrint) => dropOffRows(p.device, p.precheck).filter((r) => r.value === 'bad').map((r) => r.label);

/**
 * Technician ticket, option A: the label RepairDesk prints on the DYMO LabelWriter, 89 x 29 mm
 * (DYMO 30252). The logo and barcode on the left; the ticket number, when it's due, the customer,
 * the device, the work and anything not working at drop-off on the right; it ends with "Don't
 * Share Passcode". The passcode itself is never printed: it's in STORE OS ("Show passcode").
 */
export function RepairTechLabel({ p }: { p: TicketPrint }) {
  const logo = techLogo(p.logo_url);
  const failed = failedChecks(p);
  const pay = repairPayment(p);
  return (
    <div className="tklabel tkA">
      <div className="tkA-left">
        {logo ? <div className="tkA-logo"><img src={logo} alt={p.business_name} /></div> : <div className="tkA-biz">{p.business_name}</div>}
        <div className="tkA-bar"><Barcode value={p.ticket} height={28} /></div>
      </div>
      <div className="tkA-right">
        <div className="tk-top">
          <span className="tk-no">{p.ticket}</span>
          <span className="tk-due">{[shortTime(p.repair_time), p.promised_at ? `Due ${dueText(p.promised_at)}` : null].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="tk-name">{p.customer.name}{p.customer.phone ? <span className="tk-ph"> · {formatPhone(p.customer.phone)}</span> : null}</div>
        <div className="tk-dev"><b>{shortDeviceName(p.device)}</b>{p.device.color ? ` · ${p.device.color}` : ''}</div>
        <div className="tk-work">{labelWork(p)}</div>
        {failed.length ? <div className="tk-bad">✗ {failed.join(', ')}</div> : null}
      </div>
      <div className="tk-foot">Don't Share Passcode<span className={`tk-pay ${pay.state}`}>{pay.tag}</span></div>
    </div>
  );
}

/**
 * Technician ticket, option B: the 4-inch DYMO label (30256, 2-5/16 x 4 in): the logo, a TECH
 * TICKET bar, the ticket number and barcode, when it's due, the customer, the device and IMEI, the
 * problem, the work to do with boxes to tick, the drop-off check, the damage and the passcode note.
 */
export function TechTicketTall({ p }: { p: TicketPrint }) {
  const logo = techLogo(p.logo_url);
  const pay = repairPayment(p);
  return (
    <div className="tkB">
      {logo ? <div className="tkB-logo"><img src={logo} alt={p.business_name} /></div> : <div className="tkB-biz">{p.business_name}</div>}
      <div className="tkB-bar">TECH TICKET</div>
      <div className="tkB-mid">
      <div className="tkB-top">
        <span className="tkB-no">{p.ticket}</span>
        <span className="tkB-due">{p.promised_at ? <b>Due {dueText(p.promised_at)}</b> : <b>No rush</b>}{p.repair_time ? <span>{p.repair_time}</span> : null}</span>
      </div>
      <div className="tkB-code"><Barcode value={p.ticket} height={36} /></div>
      <div className={`tkB-pay ${pay.state}`}>{pay.tag} · {pay.detail}</div>
      <div className="tkB-row"><b>{p.customer.name}</b>{p.customer.phone ? ` · ${formatPhone(p.customer.phone)}` : ''}</div>
      <div className="tkB-row"><b>{shortDeviceName(p.device)}</b>{p.device.color ? ` · ${p.device.color}` : ''}</div>
      {p.device.imei || p.device.serial ? <div className="tkB-row sm">{p.device.imei ? `IMEI ${p.device.imei}` : `Serial ${p.device.serial}`}</div> : null}
      <div className="tkB-prob"><b>Problem:</b> {p.issue}</div>
      <div className="tkB-h">WORK TO DO</div>
      {(p.lines.length ? p.lines.slice(0, 2) : [{ description: 'Diagnose and price it', part: null }]).map((l, i) => (
        <div key={i} className="tkB-todo"><span className="chk" /><span>{l.description}{l.part ? <span className="dim"> · {l.part}</span> : null}</span></div>
      ))}
      {p.lines.length > 2 ? <div className="tkB-row sm">+ {p.lines.length - 2} more on the ticket</div> : null}
      <DropOffCheck p={p} compact />
      {p.condition_notes ? <div className="tkB-row sm"><b>Damage:</b> {p.condition_notes}</div> : null}
      <div className="tkB-row sm">{p.passcode_note.startsWith('Passcode on file') ? 'Passcode on file: “Show passcode” in STORE OS' : p.passcode_note}</div>
      </div>
      <div className="tk-foot">Don't Share Passcode</div>
    </div>
  );
}

/**
 * Technician repair ticket on receipt paper (80 mm), in the same design as the customer's repair
 * ticket: the logo, TECH REPAIR TICKET, the ticket number and barcode, when it's due, the customer
 * and device, the problem, the drop-off check, the repairs (and the part used), and it ends right
 * after "Don't Share Passcode" (no policies: it stays with the phone, not the customer).
 */
export function TechTicketReceipt({ p }: { p: TicketPrint }) {
  const pay = repairPayment(p);
  return (
    <div className="receipt tkR">
      <ReceiptHeader logo={p.logo_url} business={p.business_name} store={p.store} website={p.website} />
      <div className="c strong tkR-title">TECH REPAIR TICKET</div>
      <div className="copy-tag">TECHNICIAN COPY</div>
      <div className="ticket-no">{p.ticket}</div>
      <div className="bar"><Barcode value={p.ticket} /></div>
      <hr />
      <PayStampView pay={pay} />
      <hr />
      <dl className="meta">
        <dt>Dropped off</dt><dd>{formatDateTime(p.created_at)}</dd>
        {p.repair_time ? <><dt>Repair time</dt><dd className="strong">{p.repair_time}</dd></> : null}
        {p.promised_at ? <><dt>Ready by</dt><dd className="strong">{formatDateTime(p.promised_at)}</dd></> : null}
        <dt>Customer</dt><dd>{p.customer.name}</dd>
        {p.customer.phone ? <><dt>Phone</dt><dd>{formatPhone(p.customer.phone)}</dd></> : null}
        <dt>Device</dt><dd>{shortDeviceName(p.device)}{p.device.color ? ` · ${p.device.color}` : ''}</dd>
        {p.device.imei ? <><dt>IMEI</dt><dd>{p.device.imei}</dd></> : p.device.serial ? <><dt>Serial</dt><dd>{p.device.serial}</dd></> : null}
        {p.employee ? <><dt>Checked in by</dt><dd>{p.employee}</dd></> : null}
        <dt>Technician</dt><dd>{p.technician ?? 'Anyone'}</dd>
      </dl>
      <hr />
      <div><b>Problem:</b> {p.issue}</div>
      {p.left_with.length ? <div className="dim">Left with it: {p.left_with.join(', ')}</div> : null}
      <DropOffCheck p={p} />
      <hr />
      <table className="rtable">
        <thead><tr><th>REPAIR</th><th className="r">PRICE</th></tr></thead>
        <tbody>
          {p.lines.length ? p.lines.map((l, i) => <tr key={i}><td>{l.description}{l.part ? <div className="dim">Part: {l.part}</div> : null}</td><td className="r">{formatCents(l.price_cents)}</td></tr>)
            : <tr><td>Diagnose and price it</td><td className="r" /></tr>}
        </tbody>
      </table>
      <hr />
      <div className="rl"><span>Repair price</span><span>{formatCents(p.totals.subtotal_cents)}</span></div>
      <div className="rl"><span>{p.totals.tax_removed ? 'Tax removed' : 'Sales tax (parts)'}</span><span>{formatCents(p.totals.tax_cents)}</span></div>
      <div className="rl tot"><span>{p.paid ? 'TOTAL' : 'ESTIMATE'}</span><span>{formatCents(p.totals.total_cents)}</span></div>
      <div className="rl strong"><span>{pay.tag}</span><span>{pay.detail}</span></div>
      {p.condition_notes || p.passcode_note ? <hr /> : null}
      {p.condition_notes ? <div><b>Damage:</b> {p.condition_notes}</div> : null}
      <div className="dim">{p.passcode_note.startsWith('Passcode on file') ? 'Passcode on file: “Show passcode” in STORE OS' : p.passcode_note}</div>
      <div className="tkC-foot">Don't Share Passcode</div>
    </div>
  );
}

/** The same ticket on a full 8.5 x 11 page. */
export function RepairTicketLetter({ p }: { p: TicketPrint }) {
  const checks = precheckText(p.precheck);
  const pay = repairPayment(p);
  return (
    <div className="invoice">
      <div className="ih">
        <div>
          {p.logo_url ? <img src={p.logo_url} alt={p.business_name} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} /> : null}
          <div className="biz">{p.business_name}</div>
          <div>{p.store.name} store</div>
          {storeLines(p.store).map((l) => <div key={l}>{l}</div>)}
          <div>{[p.store.phone ? formatPhone(p.store.phone) : null, p.website].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="right">
          <h1>REPAIR TICKET</h1>
          <div className="copy-tag">CUSTOMER COPY</div>
          <div className="strong" style={{ fontSize: 17 }}>{p.ticket}</div>
          <div>{formatDateTime(p.created_at)}</div>
          <div className="barc"><Barcode value={p.ticket} height={40} /></div>
        </div>
      </div>
      <div className="two">
        <div>
          <div className="lab">Customer</div>
          <div className="strong">{p.customer.name}</div>
          <div>{formatPhone(p.customer.phone)}</div>
        </div>
        <div>
          <div className="lab">Device</div>
          <div className="strong">{p.device.brand} {p.device.model}{p.device.color ? ` · ${p.device.color}` : ''}</div>
          {p.device.imei ? <div>IMEI {p.device.imei}</div> : p.device.serial ? <div>Serial {p.device.serial}</div> : null}
          {p.repair_time ? <div className="strong">{p.repair_time}</div> : null}
          {p.promised_at ? <div>Ready by {formatDateTime(p.promised_at)}</div> : null}
        </div>
      </div>
      <div className="two">
        <div><div className="lab">Problem</div><div>{p.issue}</div>{p.condition_notes ? <><div className="lab" style={{ marginTop: 8 }}>Condition at drop-off</div><div>{p.condition_notes}</div></> : null}</div>
        <div>{checks ? <><div className="lab">Drop-off check</div><div>{checks}</div></> : null}{p.left_with.length ? <div>Left with it: {p.left_with.join(', ')}</div> : null}<div style={{ color: '#555', marginTop: 6 }}>{p.passcode_note}</div></div>
      </div>
      <table>
        <thead><tr><th>Repair</th><th>Warranty</th><th className="r">Price</th></tr></thead>
        <tbody>{p.lines.map((l, i) => <tr key={i}><td className="strong">{l.description}</td><td>{l.warranty ?? ''}</td><td className="r">{formatCents(l.price_cents)}</td></tr>)}</tbody>
      </table>
      <div className="totals">
        <div><span>Repair price</span><span>{formatCents(p.totals.subtotal_cents)}</span></div>
        <div><span>{p.totals.tax_removed ? 'Tax removed' : 'Sales tax (parts)'}</span><span>{formatCents(p.totals.tax_cents)}</span></div>
        <div className="grand"><span>{p.paid ? 'Total' : 'Estimate'}</span><span>{formatCents(p.totals.total_cents)}</span></div>
        <div className="pay-line"><span className={`pay-stamp sm ${pay.state}`}>{pay.tag}</span><span>{pay.detail}</span></div>
      </div>
      <ReceiptPolicies title="REPAIR POLICIES" text={p.terms} />
      <SiteLine website={p.website} />
      <ReceiptMark />
    </div>
  );
}
